// Three.js renderer, visual pass 4: per-type procedural unit models (helms, banners, tower
// shields, bespoke ballista), walk/attack animation, articulated boss rigs with giant gait +
// camera rumble (bossRigs.ts), directional shadows + ACES/IBL material lighting, particles,
// procedural faction rigs, and audited imported CC0 environment assets.

import * as THREE from 'three';
import { PostFx } from './postFx';
import { DEFAULT_SKY, makeSkyDome, makeSkyEnvironment, type SkyDome } from './sky';
import { GROUND_DETAIL_METRES, makeGroundDetailMaps, type GroundDetailMaps } from './groundTextures';
import { DecalField, SPRITE, Vfx } from './vfx';
import { applyRigFlash } from './hitFlash';
import { buildingDef, spellDef, unitDef } from '../content/registry';
import { TUNING } from '../content/tuning';
import type { PlayerId } from '../content/types';
import type { Vec2 } from '../shared/vec';
import type { Sim } from '../sim/sim';
import type { Building, EntId, Projectile, SimEvent, Squad } from '../sim/state';
import { footprintOf, squadMembers } from '../sim/state';
import { ParticleSystem } from './particles';
import { animateBoss, makeBossRig, newBossAnim } from './bossRigs';
import type { BossAnimState, BossRig } from './bossRigs';
import {
  OWNER_COLORS,
  STYLES,
  makeArmGeo,
  makeBallistaGroup,
  makeBannerGeo,
  makeBowGeo,
  makeBonesGeo,
  makeCorpseGeo,
  makeCrossbowGeo,
  makeGiantCorpseGeo,
  makeHatGeo,
  makeHeaterShieldGeo,
  makeHelmDomeGeo,
  makeHelmSpikeGeo,
  makeLegGeo,
  makeRifleGeo,
  makeTankGroup,
  makeTankWreckGeo,
  makeTorsoGeo,
  makeTowerShieldGeo,
} from './unitStyles';
import { makeMountRig, MOUNTED_DEFS, isFlyingMount } from './mountRigs';
import { makeWorkerRig, BESPOKE_WORKER_DEFS, isHoverWorker, poseBarkwrightCircle } from './workerRigs';
import { BESPOKE_BEAST_DEFS, makeBeastRig, poseBeastRig } from './beastRigs';
import {
  BESPOKE_VERDANT_SQUAD_DEFS,
  makeVerdantSquadRig,
  type VerdantAbilityCue,
  type VerdantSquadRig,
} from './verdantSquadRigs';
import {
  BESPOKE_IRON_SQUAD_DEFS,
  makeFortressRamGroup,
  makeIronSquadRig,
  type IronSquadRig,
} from './ironCovenantRigs';
import {
  isBespokeFactionSquad,
  makeFactionSquadRig,
  type FactionSquadRig,
} from './factionSquadRigs';
import { BESPOKE_BUILDING_DEFS, makeBespokeBuildingRig } from './buildingRigs';
import { makeRockFormation, populateDepositModel, populateRubbleModel } from './terrainRigs';

export { OWNER_COLORS };

const MAX_UNITS = 1200;
const MAX_CORPSES = 260;
const MAX_BIG_CORPSES = 24; // TEST — giant bodies / machine wrecks on the field at once
// corpse decay timeline (AoE-style): topple, body lingers a minute, then a bone pile
// that persists for the rest of the match (owner request) — recycled only when the field
// saturates the corpse cap
const CORPSE_FALL_T = 0.55;
const CORPSE_BODY_T = 60;
const CORPSE_SINK_T = 3;
const MAX_BARS = 300;
const MAX_RINGS = 160;

// Render layers. The world goes through the post-processing composer; the overlay is drawn
// straight to the canvas afterwards so its toneMapped:false materials stay exact.
const LAYER_WORLD = 0;
const LAYER_OVERLAY = 1;

// Decal tints. Soot and dried blood read as ground damage without pulling toward any faction hue.
const DECAL_SCORCH = new THREE.Color(0x1a1611);
const DECAL_STAIN = new THREE.Color(0x2a1512);

/** How long a struck unit flashes white. // TEST */
const HIT_FLASH_SECONDS = 0.12;
const FLASH_WHITE = new THREE.Color(0xffffff);
const MAX_PROJ = 160;
const MAX_MINES = 64;
const MAX_HELMS = 600;
const MAX_BANNERS = 200;
const TRAIL_SEGS = 3; // fading quads per projectile

/** per-squad render-side animation state (sim stays untouched) */
interface SquadAnim {
  walkPhase: number;
  wheelRoll: number;
  prevCooldown: number;
  attackT: number;
  /** attacks seen so far — giants cycle melee move variants off this */
  attackCount: number;
  /** smoothed yaw so squads turn instead of snapping */
  yaw: number;
  hasYaw: boolean;
  /** capture-channel timeline (arms pumping toward the site) */
  captureT: number;
  /** countdown to the next capture wisp fx */
  nextCaptureFx: number;
  /** member count last frame — drops spawn a body at the vacated slot */
  prevMembers?: number;
}

/** shortest-arc angle lerp */
function lerpAngle(a: number, b: number, t: number): number {
  let d = (b - a) % (Math.PI * 2);
  if (d > Math.PI) d -= Math.PI * 2;
  if (d < -Math.PI) d += Math.PI * 2;
  return a + d * Math.min(1, t);
}

/** Render-only fall/hold/get-up curve for the authoritative sim knockdown timer. */
function knockdownPose(s: Squad): number {
  if (s.knockdownRemaining <= 0 || s.knockdownTotal <= 0) return 0;
  const progress = 1 - s.knockdownRemaining / s.knockdownTotal;
  const fall = Math.min(1, progress / 0.18);
  const rise = Math.max(0, Math.min(1, (progress - 0.78) / 0.22));
  return fall * (1 - rise);
}

interface Fx {
  pos: Vec2;
  ttl: number;
  maxTtl: number;
  color: number;
  radius: number;
  /** contract instead of expand (order-marker style) */
  shrink?: boolean;
  /** seconds before the fx becomes visible (staggered double-pulse) */
  delay?: number;
}

interface VerdantGroundVisual {
  group: THREE.Group;
  ttl: number;
  maxTtl: number;
}

/** click/order feedback marker kinds → classic RTS colors */
export type OrderMarkerKind =
  | 'move'
  | 'attack'
  | 'capture'
  | 'claim'
  | 'rally'
  | 'repair'
  | 'escort'
  | 'mount'
  | 'withdraw';

// TEST — marker palette: green move, red attack, gold capture, cyan utility
const ORDER_MARKER_COLORS: Record<OrderMarkerKind, number> = {
  move: 0x3fd05a,
  attack: 0xff3b2e,
  capture: 0xffd76e,
  claim: 0xffd76e,
  rally: 0x9fe8ff,
  repair: 0x9fe8ff,
  escort: 0x9fe8ff,
  mount: 0x9fe8ff,
  withdraw: 0xcccccc,
};

/** A fallen unit left on the field: topples, lies for a minute, then becomes bones. */
interface Corpse {
  /** body = soldier ragdoll; giant = single large body; wreck = charred machine, no bones */
  kind: 'body' | 'giant' | 'wreck';
  x: number;
  z: number;
  /** resting yaw (head direction of the fallen body) */
  yaw: number;
  scale: number;
  color: THREE.Color;
  age: number;
  /** per-corpse topple duration so squadmates don't fall in lockstep */
  fallDur: number;
}

export interface SelectionView {
  squads: EntId[];
  workers: EntId[];
  buildingId: EntId | null;
  /** read-only info target — enemy units/buildings, deposits, wall foundations */
  inspect: { kind: 'enemySquad' | 'enemyBuilding' | 'deposit' | 'ruin'; id: EntId } | null;
}

function mixColor(a: number, b: number, t: number): THREE.Color {
  return new THREE.Color(a).lerp(new THREE.Color(b), t);
}

function disposeGroup(group: THREE.Object3D): void {
  const geometries = new Set<THREE.BufferGeometry>();
  const materials = new Set<THREE.Material>();
  group.traverse((o) => {
    if (!(o instanceof THREE.Mesh)) return;
    geometries.add(o.geometry);
    const owned = Array.isArray(o.material) ? o.material : [o.material];
    for (const material of owned) materials.add(material);
  });
  for (const geometry of geometries) geometry.dispose();
  for (const material of materials) material.dispose();
}

interface GroundVertex {
  x: number;
  z: number;
}

/** Clip a convex ground polygon against one side of the playable map rectangle. */
function clipGroundPolygon(
  polygon: GroundVertex[],
  axis: 'x' | 'z',
  boundary: number,
  keepGreater: boolean,
): GroundVertex[] {
  if (polygon.length === 0) return polygon;
  const output: GroundVertex[] = [];
  const inside = (p: GroundVertex): boolean => keepGreater ? p[axis] >= boundary : p[axis] <= boundary;
  const intersection = (a: GroundVertex, b: GroundVertex): GroundVertex => {
    const delta = b[axis] - a[axis];
    const t = Math.abs(delta) > 1e-8 ? (boundary - a[axis]) / delta : 0;
    const result: GroundVertex = {
      x: a.x + (b.x - a.x) * t,
      z: a.z + (b.z - a.z) * t,
    };
    result[axis] = boundary;
    return result;
  };

  let previous = polygon[polygon.length - 1];
  let previousInside = inside(previous);
  for (const current of polygon) {
    const currentInside = inside(current);
    if (currentInside !== previousInside) output.push(intersection(previous, current));
    if (currentInside) output.push(current);
    previous = current;
    previousInside = currentInside;
  }
  return output;
}

/**
 * Annulus geometry clipped to the playable map. Core territory circles sit four metres
 * beyond the east/west edges, so ordinary RingGeometry visibly leaked into the void.
 */
function makeMapClippedRingGeometry(
  center: Vec2,
  innerRadius: number,
  outerRadius: number,
  mapWidth: number,
  mapHeight: number,
  segments = 64,
): THREE.BufferGeometry {
  const positions: number[] = [];
  for (let i = 0; i < segments; i++) {
    const a = (i / segments) * Math.PI * 2;
    const b = ((i + 1) / segments) * Math.PI * 2;
    let polygon: GroundVertex[] = [
      { x: center.x + Math.cos(a) * outerRadius, z: center.y + Math.sin(a) * outerRadius },
      { x: center.x + Math.cos(b) * outerRadius, z: center.y + Math.sin(b) * outerRadius },
      { x: center.x + Math.cos(b) * innerRadius, z: center.y + Math.sin(b) * innerRadius },
      { x: center.x + Math.cos(a) * innerRadius, z: center.y + Math.sin(a) * innerRadius },
    ];
    polygon = clipGroundPolygon(polygon, 'x', 0, true);
    polygon = clipGroundPolygon(polygon, 'x', mapWidth, false);
    polygon = clipGroundPolygon(polygon, 'z', 0, true);
    polygon = clipGroundPolygon(polygon, 'z', mapHeight, false);
    for (let j = 1; j < polygon.length - 1; j++) {
      for (const vertex of [polygon[0], polygon[j], polygon[j + 1]]) {
        positions.push(vertex.x, 0, vertex.z);
      }
    }
  }
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.Float32BufferAttribute(positions, 3));
  geometry.computeBoundingSphere();
  return geometry;
}

/** A rounded low-poly leaf clump for Verdant Wilds foliage (spirit tree, tree towers). */
function leafClump(mat: THREE.Material, x: number, y: number, z: number, r: number): THREE.Mesh {
  const m = new THREE.Mesh(new THREE.IcosahedronGeometry(r, 0), mat);
  m.position.set(x, y, z);
  m.scale.y = 0.82;
  m.castShadow = true;
  return m;
}

/**
 * A real, unobstructed gate wall. The faction building rigs predate gate navigation and draw
 * castle-wall meshes continuously across their whole footprint, which made a walkable gate
 * look solid and hid squads as they crossed it. Gate segments use this dedicated rig instead:
 * two leaves retract outward into the curtain and nothing spans the open aperture overhead.
 */
function makeGateWallRig(b: Building): THREE.Group {
  const d = buildingDef(b.defId);
  const group = new THREE.Group();
  group.name = 'gateWall';

  const faction = d.faction;
  const isAshen = faction === 'ashen-host';
  const isVerdant = faction === 'verdant-wilds';
  const isAstral = faction === 'astral-dominion';
  const ownerColor = OWNER_COLORS[b.owner];
  const wallColor = isAshen ? 0x67433f : isVerdant ? 0x68482d : isAstral ? 0xd8e2ef : 0xa6a39b;
  const frameColor = isAshen ? 0xa99c86 : isVerdant ? 0x3e5530 : isAstral ? 0x172039 : 0x343a43;
  const leafColor = isAshen ? 0x392b30 : isVerdant ? 0x4e3424 : isAstral ? 0x315a91 : 0x574738;
  const accentColor = isAshen ? 0xd44755 : isVerdant ? 0x9acb62 : isAstral ? 0x58d8ff : 0xc6a04c;
  const wallMat = new THREE.MeshStandardMaterial({
    color: mixColor(wallColor, ownerColor, 0.08),
    roughness: isAstral ? 0.35 : 0.9,
    metalness: isAstral ? 0.62 : 0.08,
    flatShading: true,
  });
  const frameMat = new THREE.MeshStandardMaterial({
    color: mixColor(frameColor, ownerColor, 0.1),
    roughness: isAstral ? 0.38 : 0.72,
    metalness: isAstral ? 0.7 : 0.32,
    flatShading: true,
  });
  const leafMat = new THREE.MeshStandardMaterial({
    color: mixColor(leafColor, ownerColor, 0.14),
    emissive: isAstral ? 0x173d67 : 0x000000,
    emissiveIntensity: isAstral ? 0.6 : 0,
    roughness: isAstral ? 0.3 : 0.82,
    metalness: isAstral ? 0.62 : 0.26,
    flatShading: true,
  });
  const accentMat = new THREE.MeshStandardMaterial({
    color: accentColor,
    emissive: accentColor,
    emissiveIntensity: 0.42,
    roughness: 0.38,
    metalness: isVerdant || isAshen ? 0.05 : 0.58,
    flatShading: true,
  });
  const indicatorMat = new THREE.MeshStandardMaterial({
    color: 0xff594d,
    emissive: 0xff3428,
    emissiveIntensity: 0.9,
    roughness: 0.24,
    metalness: 0.32,
    flatShading: true,
  });

  const width = d.footprint.w + (isAstral ? 0.5 : 0.7);
  const depth = d.footprint.h;
  const span = Math.min(Math.max(2, b.gateSpan), depth - 2.4);
  const flankDepth = (depth - span) / 2;
  group.userData.gateSpan = span;

  // Split both the curtain and its battlement walk. Keeping the entire aperture clear avoids
  // an overhead mesh swallowing the upper half of giants in the default top-down camera.
  for (const side of [-1, 1]) {
    const z = side * (span / 2 + flankDepth / 2);
    const curtain = new THREE.Mesh(new THREE.BoxGeometry(width, 3.75, flankDepth), wallMat);
    curtain.position.set(0, 1.875, z);
    group.add(curtain);
    const walk = new THREE.Mesh(new THREE.BoxGeometry(width + 0.5, 0.34, flankDepth), frameMat);
    walk.position.set(0, 3.92, z);
    group.add(walk);

    const first = side < 0 ? -depth / 2 + 0.55 : span / 2 + 0.55;
    const last = side < 0 ? -span / 2 - 0.55 : depth / 2 - 0.55;
    for (let p = first; p <= last + 0.01; p += 1.1) {
      const merlon = new THREE.Mesh(new THREE.BoxGeometry(width + 0.2, 0.55, 0.44), wallMat);
      merlon.position.set(0, 4.36, p);
      group.add(merlon);
    }
  }

  // Outer anchors and smaller gateposts make the opening unmistakable without intruding into
  // its dynamic clearance (currently eight metres for giant traffic). Their inner faces sit
  // at the nav aperture boundary.
  for (const z of [-depth / 2, depth / 2]) {
    const anchor = new THREE.Mesh(
      isAstral
        ? new THREE.CylinderGeometry(0.76, 1.02, 5.25, 6)
        : new THREE.CylinderGeometry(0.92, 1.16, 5.15, isVerdant ? 7 : 8),
      frameMat,
    );
    anchor.position.set(0, 2.575, z);
    group.add(anchor);
    const cap = new THREE.Mesh(
      isVerdant ? new THREE.ConeGeometry(0.68, 1.15, 7) : new THREE.OctahedronGeometry(0.58, 0),
      accentMat,
    );
    cap.position.set(0, 5.62, z);
    group.add(cap);
  }
  for (const side of [-1, 1]) {
    const z = side * (span / 2 + 0.38);
    const post = new THREE.Mesh(
      new THREE.CylinderGeometry(0.36, 0.48, 4.55, isAstral ? 6 : 8),
      frameMat,
    );
    post.position.set(0, 2.275, z);
    group.add(post);
    const indicator = new THREE.Mesh(new THREE.OctahedronGeometry(0.24, 0), indicatorMat);
    indicator.name = 'gateIndicator';
    indicator.position.set(0, 4.85, z);
    group.add(indicator);
  }

  // The two halves slide along the wall's long axis into pockets behind the curtain. Unlike
  // swinging doors or a raised portcullis, fully retracted leaves cannot overlap passing units.
  const leafDepth = span / 2 - 0.16;
  for (const side of [-1, 1]) {
    const leaf = new THREE.Group();
    leaf.name = side < 0 ? 'gateLeafNegative' : 'gateLeafPositive';
    const closedZ = side * span / 4;
    leaf.position.set(0, 0, closedZ);
    leaf.userData.closedZ = closedZ;
    leaf.userData.openZ = side * (span / 2 + 0.24);

    const panel = new THREE.Mesh(new THREE.BoxGeometry(width * 0.72, 3.18, leafDepth), leafMat);
    panel.position.y = 1.59;
    leaf.add(panel);
    for (const y of [0.48, 1.58, 2.68]) {
      const band = new THREE.Mesh(new THREE.BoxGeometry(width * 0.82, 0.13, leafDepth + 0.06), accentMat);
      band.position.y = y;
      leaf.add(band);
    }
    // Faction-readable vertical detail: bars, ribs, roots, or hardlight seams.
    const detailCount = isVerdant ? 4 : isAshen ? 3 : 5;
    for (let i = 0; i < detailCount; i++) {
      const z = -leafDepth / 2 + (i + 0.5) * (leafDepth / detailCount);
      const detail = new THREE.Mesh(
        isVerdant
          ? new THREE.CylinderGeometry(0.08, 0.13, 3.25, 5)
          : new THREE.BoxGeometry(width * (isAstral ? 0.8 : 0.62), 3.22, isAstral ? 0.045 : 0.1),
        isAshen ? wallMat : accentMat,
      );
      detail.position.set(0, 1.61, z);
      if (isVerdant) detail.rotation.z = (i % 2 ? 1 : -1) * 0.08;
      leaf.add(detail);
    }
    group.add(leaf);
  }

  // A low threshold remains visible while open, communicating ownership without occlusion.
  const threshold = new THREE.Mesh(
    new THREE.PlaneGeometry(width * 1.15, span - 0.2),
    new THREE.MeshBasicMaterial({ color: ownerColor, transparent: true, opacity: 0.18, depthWrite: false }),
  );
  threshold.name = 'gateThreshold';
  threshold.rotation.x = -Math.PI / 2;
  threshold.position.y = 0.025;
  group.add(threshold);

  return group;
}

const OWNER_COLORS_T = OWNER_COLORS.map((color) => new THREE.Color(color));
const WORKER_HEAD = new THREE.Color(0xc99b72);

// member slot offsets by count (§17.1 wedge)
function memberOffsets(count: number): Vec2[] {
  if (count <= 1) return [{ x: 0, y: 0 }];
  if (count === 3)
    return [
      { x: 0, y: 0.8 },
      { x: -0.8, y: -0.5 },
      { x: 0.8, y: -0.5 },
    ];
  const out: Vec2[] = [];
  const cols = 3;
  for (let i = 0; i < count; i++) {
    const row = Math.floor(i / cols);
    const col = i % cols;
    out.push({ x: (col - 1) * 1.0, y: (row - 0.5) * 1.1 });
  }
  return out;
}

const SPROUTLING_MEMBER_OFFSETS: Vec2[] = [
  { x: -1, y: -1 }, { x: 0, y: -1 }, { x: 1, y: -1 },
  { x: -1, y: 0 }, { x: 0, y: 0 }, { x: 1, y: 0 },
  { x: -0.5, y: 1 }, { x: 0.5, y: 1 },
];

/** Procedural mottled ground texture: layered noise, grass speckles, worn lanes, vignette. */
function makeGroundTexture(w: number, h: number): THREE.CanvasTexture {
  const canvas = document.createElement('canvas');
  canvas.width = 2048;
  canvas.height = Math.round((2048 * h) / w);
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#41503b';
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  // Macro variation uses feathered gradients. Flat translucent circles looked like a
  // screen-space bokeh filter once tone mapping lifted the grass highlights.
  for (let i = 0; i < 190; i++) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    const r = 60 + Math.random() * 130;
    const warm = Math.random() > 0.5;
    const red = warm ? (88 + Math.random() * 24) | 0 : (38 + Math.random() * 18) | 0;
    const green = warm ? (90 + Math.random() * 20) | 0 : (66 + Math.random() * 22) | 0;
    const blue = warm ? 53 : (45 + Math.random() * 14) | 0;
    const alpha = 0.045 + Math.random() * 0.045;
    const patch = ctx.createRadialGradient(x, y, 0, x, y, r);
    patch.addColorStop(0, `rgba(${red},${green},${blue},${alpha})`);
    patch.addColorStop(0.55, `rgba(${red},${green},${blue},${alpha * 0.48})`);
    patch.addColorStop(1, `rgba(${red},${green},${blue},0)`);
    ctx.fillStyle = patch;
    ctx.fillRect(x - r, y - r, r * 2, r * 2);
  }
  // mid mottling
  for (let i = 0; i < 14000; i++) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    const r = 2 + Math.random() * 11;
    const g = 60 + Math.random() * 40;
    const rr = 50 + Math.random() * 28;
    ctx.fillStyle = `rgba(${rr | 0},${g | 0},${(45 + Math.random() * 20) | 0},${0.05 + Math.random() * 0.09})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // worn center lane, roughened with jittered blotches
  const laneY = canvas.height / 2;
  const grad = ctx.createLinearGradient(0, laneY - 110, 0, laneY + 110);
  grad.addColorStop(0, 'rgba(120,110,85,0)');
  grad.addColorStop(0.5, 'rgba(120,110,85,0.26)');
  grad.addColorStop(1, 'rgba(120,110,85,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, laneY - 110, canvas.width, 220);
  for (let i = 0; i < 900; i++) {
    const x = Math.random() * canvas.width;
    const y = laneY + (Math.random() - 0.5) * 190;
    const r = 3 + Math.random() * 14;
    ctx.fillStyle = `rgba(${(115 + Math.random() * 20) | 0},${(105 + Math.random() * 18) | 0},${(80 + Math.random() * 15) | 0},${0.05 + Math.random() * 0.09})`;
    ctx.beginPath();
    ctx.arc(x, y, r, 0, Math.PI * 2);
    ctx.fill();
  }
  // (owner feedback: the two curved N/S path arcs read as an "eyeball" — removed for now)
  // grass speckles: tiny bright blades in a few greens
  const blades = ['#5c7048', '#657a4b', '#4e6240', '#71865a'];
  for (let i = 0; i < 5000; i++) {
    const x = Math.random() * canvas.width;
    const y = Math.random() * canvas.height;
    if (Math.abs(y - laneY) < 70 && Math.random() < 0.7) continue; // thinner on the lane
    ctx.fillStyle = blades[(Math.random() * blades.length) | 0];
    ctx.globalAlpha = 0.25 + Math.random() * 0.4;
    ctx.fillRect(x, y, 1.5 + Math.random() * 2, 2 + Math.random() * 3.5);
  }
  ctx.globalAlpha = 1;
  // radial edge vignette so the map border reads
  const vg = ctx.createRadialGradient(
    canvas.width / 2,
    canvas.height / 2,
    Math.min(canvas.width, canvas.height) * 0.42,
    canvas.width / 2,
    canvas.height / 2,
    Math.max(canvas.width, canvas.height) * 0.72,
  );
  vg.addColorStop(0, 'rgba(12,18,14,0)');
  vg.addColorStop(1, 'rgba(12,18,14,0.42)');
  ctx.fillStyle = vg;
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  tex.anisotropy = 8;
  return tex;
}

const STONE = 0x8d8578;
const STONE_DARK = 0x6b655c;
const ROOF = 0x5d4a3a;

export class GameRenderer {
  readonly renderer: THREE.WebGLRenderer;
  readonly scene: THREE.Scene;
  readonly camera: THREE.PerspectiveCamera;
  private environmentMap: THREE.Texture;
  /** desired pan target, written by input; the camera eases toward it */
  camTarget: Vec2;
  /** desired zoom distance, written by input */
  zoom = 42;
  /** smoothed values the camera is actually placed at */
  private camSmooth: Vec2;
  private zoomSmooth = 42;
  /** the zoom the camera is currently placed at, for input scaling that must match the view */
  get smoothedZoom(): number {
    return this.zoomSmooth;
  }
  /** 0 = instant (old behaviour), 1 = default easing; from the Camera Smoothing setting */
  private cameraSmoothing = 1;

  // instanced pools
  private bodyInst: THREE.InstancedMesh;
  private headInst: THREE.InstancedMesh;
  private legInst: THREE.InstancedMesh;
  private armInst: THREE.InstancedMesh;
  private weaponInst: THREE.InstancedMesh;
  private bowInst: THREE.InstancedMesh;
  private crossbowInst: THREE.InstancedMesh;
  private rifleInst: THREE.InstancedMesh;
  private shieldInst: THREE.InstancedMesh;
  private heaterShieldInst: THREE.InstancedMesh;
  private carryInst: THREE.InstancedMesh;
  private shadowInst: THREE.InstancedMesh;
  private helmDomeInst: THREE.InstancedMesh;
  private helmSpikeInst: THREE.InstancedMesh;
  private hatInst: THREE.InstancedMesh;
  private bannerInst: THREE.InstancedMesh;
  private barBg: THREE.InstancedMesh;
  private barFg: THREE.InstancedMesh;
  private ringInst: THREE.InstancedMesh;
  /** bright pulsing highlight on the primary selected/inspected target (owner request) */
  private highlightRing!: THREE.Mesh;
  private projInst: THREE.InstancedMesh;
  private trailInst: THREE.InstancedMesh;
  private mineInst: THREE.InstancedMesh;
  private corpseInst: THREE.InstancedMesh;
  private boneInst: THREE.InstancedMesh;
  private giantCorpseInst: THREE.InstancedMesh;
  private wreckInst: THREE.InstancedMesh;

  private sun: THREE.DirectionalLight;
  /** cool fill opposite the sun; separates unit silhouettes from the ground */
  private rim: THREE.DirectionalLight;
  private skyDome: SkyDome;
  /** null on Low, where the frame is a direct forward render exactly as before */
  private postFx: PostFx | null = null;
  private groundDetail: GroundDetailMaps | null = null;
  private vfx: Vfx;
  private decals: DecalField;
  /** launch point per projectile, tracked render-side so the sim's Projectile is untouched */
  private projOrigin = new Map<EntId, Vec2>();
  /** scratch colour so hit flash never mutates the cached THREE.Color objects in STYLES */
  private flashColor = new THREE.Color();
  /** 0..1 flash amount for the squad currently being written into the instanced pools */
  private currentFlash = 0;
  /** quantised shadow-frustum half-extent, so we only rebuild the projection when it changes */
  private shadowHalf = 0;
  private particles: ParticleSystem;
  private groundTexture!: THREE.CanvasTexture;
  private anim = new Map<EntId, SquadAnim>();
  /** smoothed per-worker heading so workers face where they walk */
  private workerYaw = new Map<EntId, number>();
  private buildingMeshes = new Map<EntId, THREE.Group>();
  private bossRigs = new Map<EntId, BossRig>();
  private bossAnim = new Map<EntId, BossAnimState>();
  /** camera rumble amplitude (giant footfalls, slams, summons) */
  private shakeAmp = 0;
  private cameraShakeEnabled = true;
  private healthBarsEnabled = true;
  private reducedEffects = false;
  /** boss giants with a sim-confirmed crush queued (GiantCrush events) */
  private pendingCrush = new Set<EntId>();
  /** fallen bodies decaying on the field */
  private corpses: Corpse[] = [];
  private tmpColor = new THREE.Color();
  private ballistaMeshes = new Map<EntId, THREE.Group>();
  private tankMeshes = new Map<EntId, THREE.Group>();
  private fortressRamMeshes = new Map<EntId, THREE.Group>();
  /** mounted cavalry/rider rigs: one bespoke rider-on-mount group per squad member */
  private mountMeshes = new Map<EntId, THREE.Group[]>();
  /** bespoke quadruped packs: one animated beast group per living squad member */
  private beastMeshes = new Map<EntId, THREE.Group[]>();
  /** bespoke plant/fungal/spirit squads, retaining per-member variation and pose closures */
  private verdantSquadRigs = new Map<EntId, VerdantSquadRig[]>();
  /** fortress constructs and individually armed royal champions */
  private ironSquadRigs = new Map<EntId, IronSquadRig[]>();
  /** assembled undead and precision-built Astral members with faction-specific motion */
  private factionSquadRigs = new Map<EntId, FactionSquadRig[]>();
  private verdantAbilityCues = new Map<EntId, { cue: Exclude<VerdantAbilityCue, null>; remaining: number }>();
  /** bespoke per-faction worker rigs (Ashen/Verdant/Astral; IC stays instanced) */
  private workerRigMeshes = new Map<EntId, THREE.Group>();
  private ruinMeshes = new Map<EntId, THREE.Group>();
  private siteDiscs: THREE.Mesh[] = [];
  private siteRings: THREE.Mesh[] = [];
  private siteObelisks: THREE.Group[] = [];
  private zoneMeshes = new Map<EntId, THREE.Mesh>();
  private fx: Fx[] = [];
  private fxMeshes: THREE.Mesh[] = [];
  private verdantGroundVisuals: VerdantGroundVisual[] = [];
  private ghostMesh: THREE.Mesh;
  private rallyMarker: THREE.Group;
  private raycaster = new THREE.Raycaster();
  private groundPlane = new THREE.Plane(new THREE.Vector3(0, 1, 0), 0);
  private tmpMat = new THREE.Matrix4();
  private tmpQuat = new THREE.Quaternion();
  private tmpQuat2 = new THREE.Quaternion();
  private tmpScale = new THREE.Vector3();
  private tmpPos = new THREE.Vector3();
  private euler = new THREE.Euler();

  constructor(private container: HTMLElement, private sim: Sim) {
    this.renderer = new THREE.WebGLRenderer({ antialias: true, powerPreference: 'high-performance' });
    this.renderer.setSize(container.clientWidth, container.clientHeight);
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2));
    this.renderer.toneMapping = THREE.ACESFilmicToneMapping;
    this.renderer.toneMappingExposure = 0.88;
    this.renderer.outputColorSpace = THREE.SRGBColorSpace;
    this.renderer.shadowMap.enabled = true;
    this.renderer.shadowMap.type = THREE.PCFSoftShadowMap;
    container.appendChild(this.renderer.domElement);

    this.scene = new THREE.Scene();
    // Gradient sky dome instead of a flat background fill. Fog now dissolves distant geometry
    // into the dome's horizon band rather than into a slab of unrelated colour, so the map rim
    // reads as distance instead of as an edge.
    this.skyDome = makeSkyDome();
    this.scene.add(this.skyDome.mesh);
    this.scene.fog = new THREE.Fog(DEFAULT_SKY.horizon.clone().multiplyScalar(0.55).getHex(), 210, 470);
    // Outdoor IBL. RoomEnvironment is an indoor studio box, which is why materials read
    // showroom-lit; this probe has sky above and ground below.
    this.environmentMap = makeSkyEnvironment(this.renderer);
    this.scene.environment = this.environmentMap;
    this.scene.environmentIntensity = 0.56;

    this.camera = new THREE.PerspectiveCamera(48, container.clientWidth / container.clientHeight, 1, 700);
    const start = sim.map.corePositions[0];
    this.camTarget = { x: start.x, y: start.y };
    this.camSmooth = { x: start.x, y: start.y };

    // lighting: hemisphere sky bounce + shadow-casting sun (follows the camera in update())
    // A restrained fill plus a stronger key preserves shaded faces. The previous near-1.0
    // hemisphere + ambient + high exposure flattened every material into the same pale value.
    this.scene.add(new THREE.HemisphereLight(0xcfe0ff, 0x30291f, 0.38));
    this.sun = new THREE.DirectionalLight(0xffe4bd, 2.15);
    this.sun.position.set(80, 140, 60);
    this.sun.castShadow = true;
    this.sun.shadow.mapSize.set(2048, 2048);
    this.sun.shadow.camera.left = -70;
    this.sun.shadow.camera.right = 70;
    this.sun.shadow.camera.top = 70;
    this.sun.shadow.camera.bottom = -70;
    this.sun.shadow.camera.near = 20;
    this.sun.shadow.camera.far = 320;
    this.sun.shadow.bias = -0.00018;
    this.sun.shadow.normalBias = 0.08;
    this.scene.add(this.sun);
    this.scene.add(this.sun.target);
    // Rim/fill from roughly opposite the sun. Top-down dark units against dark ground had
    // nothing separating their silhouettes; this is the cheap half of what an outline pass
    // would do, and unlike an outline pass its cost does not scale with army size.
    this.rim = new THREE.DirectionalLight(0x8fb4ff, 0.42); // TEST
    this.rim.castShadow = false;
    this.scene.add(this.rim);
    this.scene.add(this.rim.target);
    this.scene.add(new THREE.AmbientLight(0xb8c5d6, 0.025));

    this.particles = new ParticleSystem(this.scene);
    this.vfx = new Vfx(this.scene);
    this.decals = new DecalField(this.scene);

    this.buildStaticWorld();

    // ---- instanced pools
    // full-bodied figures: armored torso + head + two legs + two arms per member
    this.bodyInst = this.pool(
      makeTorsoGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.75, metalness: 0.15 }),
      MAX_UNITS,
    );

    const headGeo = new THREE.SphereGeometry(0.24, 10, 8);
    headGeo.translate(0, 1.62, 0);
    this.headInst = this.pool(headGeo, new THREE.MeshStandardMaterial({ roughness: 0.65, metalness: 0.05 }), MAX_UNITS);

    this.legInst = this.pool(
      makeLegGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.1 }),
      MAX_UNITS * 2,
    );
    this.armInst = this.pool(
      makeArmGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.8, metalness: 0.1 }),
      MAX_UNITS * 2,
    );

    const weaponGeo = new THREE.ConeGeometry(1, 1, 6);
    weaponGeo.translate(0, 0.5, 0);
    this.weaponInst = this.pool(weaponGeo, new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0.7 }), MAX_UNITS);
    this.bowInst = this.pool(makeBowGeo(), new THREE.MeshStandardMaterial({ roughness: 0.82, metalness: 0.05 }), MAX_UNITS);
    this.crossbowInst = this.pool(makeCrossbowGeo(), new THREE.MeshStandardMaterial({ roughness: 0.72, metalness: 0.2 }), MAX_UNITS);
    this.rifleInst = this.pool(makeRifleGeo(), new THREE.MeshStandardMaterial({ roughness: 0.5, metalness: 0.42 }), MAX_UNITS);

    this.shieldInst = this.pool(
      makeTowerShieldGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.5, metalness: 0.5 }),
      400,
    );
    this.heaterShieldInst = this.pool(
      makeHeaterShieldGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.42, metalness: 0.58 }),
      400,
    );

    this.helmDomeInst = this.pool(
      makeHelmDomeGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0.65 }),
      MAX_HELMS,
    );
    this.helmSpikeInst = this.pool(
      makeHelmSpikeGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.35, metalness: 0.65 }),
      MAX_HELMS,
    );
    this.hatInst = this.pool(makeHatGeo(), new THREE.MeshStandardMaterial({ roughness: 0.9, metalness: 0 }), 300);
    this.bannerInst = this.pool(
      makeBannerGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.8, side: THREE.DoubleSide }),
      MAX_BANNERS,
    );

    const carryGeo = new THREE.BoxGeometry(0.4, 0.34, 0.4);
    carryGeo.translate(0, 1.15, -0.42);
    this.carryInst = this.pool(carryGeo, new THREE.MeshStandardMaterial({ roughness: 0.9 }), 128);

    const shadowGeo = new THREE.CircleGeometry(0.62, 14);
    shadowGeo.rotateX(-Math.PI / 2);
    this.shadowInst = this.pool(
      shadowGeo,
      new THREE.MeshBasicMaterial({ color: 0x000000, transparent: true, opacity: 0.18, depthWrite: false }),
      MAX_UNITS + 200,
    );
    this.shadowInst.renderOrder = 2;

    // unit parts cast shadows (blob shadows above are contact-grounding only)
    for (const inst of [
      this.bodyInst,
      this.headInst,
      this.legInst,
      this.armInst,
      this.weaponInst,
      this.bowInst,
      this.crossbowInst,
      this.rifleInst,
      this.shieldInst,
      this.heaterShieldInst,
      this.helmDomeInst,
      this.helmSpikeInst,
      this.hatInst,
      this.bannerInst,
    ]) {
      inst.castShadow = true;
    }

    const barGeo = new THREE.PlaneGeometry(1, 1);
    this.barBg = this.pool(
      barGeo,
      new THREE.MeshBasicMaterial({
        color: 0x080b08,
        transparent: true,
        opacity: 0.92,
        depthTest: false,
        depthWrite: false,
        toneMapped: false,
      }),
      MAX_BARS,
    );
    this.barFg = this.pool(
      barGeo,
      new THREE.MeshBasicMaterial({ transparent: true, depthTest: false, depthWrite: false, toneMapped: false }),
      MAX_BARS,
    );
    this.barBg.renderOrder = 10;
    this.barFg.renderOrder = 11;
    // Health bars are the only depthTest:false, toneMapped:false materials in the scene. Once a
    // composer is in play, OutputPass tone-maps the whole buffer, so `toneMapped: false` stops
    // protecting them and the pure bar greens/reds get ACES-mapped and bloomed. Move them to a
    // dedicated layer that is excluded from the composer and drawn straight to the canvas
    // afterwards, where three honours toneMapped:false again — byte-identical to the old output.
    this.barBg.layers.set(LAYER_OVERLAY);
    this.barFg.layers.set(LAYER_OVERLAY);

    const ringGeo = new THREE.RingGeometry(0.85, 1, 24);
    ringGeo.rotateX(-Math.PI / 2);
    this.ringInst = this.pool(
      ringGeo,
      new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.9, depthWrite: false }),
      MAX_RINGS,
    );
    this.ringInst.renderOrder = 5;

    // primary-selection highlight: a bright gold ground ring that pulses so the picked
    // building / resource / wall foundation is unmistakable (owner request)
    const hlGeo = new THREE.RingGeometry(0.78, 1.0, 40);
    hlGeo.rotateX(-Math.PI / 2);
    this.highlightRing = new THREE.Mesh(
      hlGeo,
      new THREE.MeshBasicMaterial({
        color: 0xffe066,
        transparent: true,
        opacity: 0.95,
        depthWrite: false,
        side: THREE.DoubleSide,
      }),
    );
    this.highlightRing.renderOrder = 6;
    this.highlightRing.visible = false;
    this.scene.add(this.highlightRing);

    // projectile: motion-oriented bolt (local +z = direction of travel) + additive trail quads
    const projGeo = new THREE.ConeGeometry(0.12, 0.9, 6);
    projGeo.rotateX(Math.PI / 2);
    // Pushed above 1.0 so the bolt clears the bloom threshold and reads as a glowing round
    // rather than a pale cone. Values >1 are meaningful now that the scene renders to an HDR
    // target and OutputPass tone-maps at the end.
    this.projInst = this.pool(
      projGeo,
      new THREE.MeshBasicMaterial({ color: new THREE.Color(0xffe9a8).multiplyScalar(2.6), toneMapped: true }),
      MAX_PROJ,
    );

    const trailGeo = new THREE.PlaneGeometry(0.55, 0.55);
    this.trailInst = this.pool(
      trailGeo,
      new THREE.MeshBasicMaterial({
        color: 0xffcf7a,
        blending: THREE.AdditiveBlending,
        transparent: true,
        opacity: 0.5,
        depthWrite: false,
      }),
      MAX_PROJ * TRAIL_SEGS,
    );
    this.trailInst.renderOrder = 7;

    const mineGeo = new THREE.CylinderGeometry(0.55, 0.68, 0.4, 10);
    this.mineInst = this.pool(mineGeo, new THREE.MeshStandardMaterial({ roughness: 0.55, metalness: 0.4 }), MAX_MINES);
    this.mineInst.castShadow = true;

    // corpses + bone piles (no cast shadows — there can be hundreds, and they hug the ground)
    this.corpseInst = this.pool(
      makeCorpseGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.05 }),
      MAX_CORPSES,
    );
    this.corpseInst.receiveShadow = true;
    this.boneInst = this.pool(
      makeBonesGeo(),
      new THREE.MeshStandardMaterial({ color: 0xd8d0c0, roughness: 0.9 }),
      MAX_CORPSES,
    );
    this.boneInst.receiveShadow = true;
    this.giantCorpseInst = this.pool(
      makeGiantCorpseGeo(),
      new THREE.MeshStandardMaterial({ roughness: 0.85, metalness: 0.05 }),
      MAX_BIG_CORPSES,
    );
    this.giantCorpseInst.receiveShadow = true;
    this.wreckInst = this.pool(
      makeTankWreckGeo(),
      new THREE.MeshStandardMaterial({ color: 0x2e2b28, roughness: 0.95, metalness: 0.25 }),
      MAX_BIG_CORPSES,
    );
    this.wreckInst.receiveShadow = true;

    // placement ghost
    this.ghostMesh = new THREE.Mesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshBasicMaterial({ color: 0x44ff88, transparent: true, opacity: 0.4, depthWrite: false }),
    );
    this.ghostMesh.visible = false;
    this.scene.add(this.ghostMesh);

    // persistent deploy/rally marker — banner pole + ground ring, shown while a
    // unit-producing building is selected so the owner can see where troops will muster
    this.rallyMarker = new THREE.Group();
    const rallyPole = new THREE.Mesh(
      new THREE.CylinderGeometry(0.07, 0.09, 2.6, 6),
      new THREE.MeshStandardMaterial({ color: 0x6b5a3b, roughness: 0.9 }),
    );
    rallyPole.position.y = 1.3;
    this.rallyMarker.add(rallyPole);
    const rallyFlag = new THREE.Mesh(
      new THREE.PlaneGeometry(1.1, 0.6),
      new THREE.MeshBasicMaterial({ color: 0x9fe8ff, side: THREE.DoubleSide, transparent: true, opacity: 0.9 }),
    );
    rallyFlag.position.set(0.55, 2.2, 0);
    this.rallyMarker.add(rallyFlag);
    const rallyRing = new THREE.Mesh(
      new THREE.RingGeometry(1.0, 1.35, 28),
      new THREE.MeshBasicMaterial({ color: 0x9fe8ff, transparent: true, opacity: 0.55, depthWrite: false }),
    );
    rallyRing.rotation.x = -Math.PI / 2;
    rallyRing.position.y = 0.06;
    this.rallyMarker.add(rallyRing);
    this.rallyMarker.visible = false;
    this.scene.add(this.rallyMarker);

    window.addEventListener('resize', this.onResize);
  }

  private pool(geo: THREE.BufferGeometry, mat: THREE.Material, count: number): THREE.InstancedMesh {
    const m = new THREE.InstancedMesh(geo, mat, count);
    m.instanceMatrix.setUsage(THREE.DynamicDrawUsage);
    m.count = 0;
    m.frustumCulled = false;
    this.scene.add(m);
    return m;
  }

  dispose(): void {
    window.removeEventListener('resize', this.onResize);
    this.groundTexture.dispose();
    this.groundDetail?.dispose();
    this.vfx.dispose();
    this.decals.dispose();
    this.skyDome.dispose();
    this.postFx?.dispose();
    this.environmentMap.dispose();
    this.renderer.dispose();
    // Matches are remounted (twice in development StrictMode), so release the discarded
    // canvas context immediately instead of waiting for browser GC/context eviction.
    this.renderer.forceContextLoss();
    this.container.removeChild(this.renderer.domElement);
  }

  applyPreferences(prefs: {
    quality: 'low' | 'medium' | 'high' | 'ultra';
    renderScale: number;
    healthBars: boolean;
    cameraShake: boolean;
    cameraSmoothing: number;
    reduceMotion: boolean;
    sceneExposure: number;
  }): void {
    const profile = prefs.quality === 'low'
      ? { pixelCap: 1, shadows: false, shadowSize: 512, anisotropy: 1, environment: 0.34, radius: 1 }
      : prefs.quality === 'medium'
        ? { pixelCap: 1.35, shadows: true, shadowSize: 1024, anisotropy: 2, environment: 0.46, radius: 1 }
        : prefs.quality === 'ultra'
          ? { pixelCap: 2.5, shadows: true, shadowSize: 4096, anisotropy: 16, environment: 0.64, radius: 2 }
          : { pixelCap: 2, shadows: true, shadowSize: 2048, anisotropy: 8, environment: 0.56, radius: 1.5 };
    // Apply render scale after the quality cap. Capping last made the entire 50–100%
    // slider a no-op on common 2x displays when Low quality capped the result at 1x.
    this.renderer.setPixelRatio(Math.min(window.devicePixelRatio, profile.pixelCap) * prefs.renderScale);
    const shadowEnableChanged =
      this.renderer.shadowMap.enabled !== profile.shadows || this.sun.castShadow !== profile.shadows;
    this.renderer.shadowMap.enabled = profile.shadows;
    this.sun.castShadow = profile.shadows;
    const shadowType = prefs.quality === 'medium' ? THREE.PCFShadowMap : THREE.PCFSoftShadowMap;
    const shadowTypeChanged = this.renderer.shadowMap.type !== shadowType;
    if (shadowTypeChanged) {
      this.renderer.shadowMap.type = shadowType;
    }
    if (shadowEnableChanged || shadowTypeChanged) {
      // Shadow defines live in compiled material programs. Recompile when Low toggles
      // shadows so changing presets is visible without remounting the match.
      this.scene.traverse((object) => {
        if (!(object instanceof THREE.Mesh)) return;
        const materials = Array.isArray(object.material) ? object.material : [object.material];
        for (const material of materials) material.needsUpdate = true;
      });
    }
    const shadowSize = profile.shadowSize;
    if (this.sun.shadow.mapSize.x !== shadowSize) {
      this.sun.shadow.map?.dispose();
      this.sun.shadow.map = null;
      this.sun.shadow.mapSize.set(shadowSize, shadowSize);
    }
    this.sun.shadow.radius = profile.radius;
    this.scene.environmentIntensity = profile.environment;
    this.renderer.toneMappingExposure = prefs.sceneExposure;
    const anisotropy = Math.min(profile.anisotropy, this.renderer.capabilities.getMaxAnisotropy());
    this.groundTexture.anisotropy = anisotropy;
    this.groundTexture.needsUpdate = true;
    // The tiled detail maps are viewed at far more oblique angles than the colour map, so they
    // benefit from anisotropic filtering at least as much.
    if (this.groundDetail) {
      this.groundDetail.normalMap.anisotropy = anisotropy;
      this.groundDetail.normalMap.needsUpdate = true;
      this.groundDetail.roughnessMap.anisotropy = anisotropy;
      this.groundDetail.roughnessMap.needsUpdate = true;
    }
    this.particles.setQuality(prefs.quality, prefs.reduceMotion);
    this.vfx.setQuality(prefs.quality, prefs.reduceMotion);
    this.decals.setQuality(prefs.quality, prefs.reduceMotion);
    this.healthBarsEnabled = prefs.healthBars;
    this.cameraShakeEnabled = prefs.cameraShake;
    this.cameraSmoothing = prefs.cameraSmoothing;
    this.reducedEffects = prefs.reduceMotion;
    if (!this.cameraShakeEnabled) this.shakeAmp = 0;

    // Post-processing is gated on the quality preset. Low bypasses the composer entirely, which
    // is both the cheapest path and the kill switch if the pipeline misbehaves: the frame is then
    // byte-identical to the pre-composer build.
    //
    // Note the composer is rebuilt rather than reconfigured on a preset change — the HDR target's
    // MSAA `samples` count is immutable after allocation.
    if (prefs.quality === 'low') {
      this.postFx?.dispose();
      this.postFx = null;
    } else if (!this.postFx?.matches(prefs.quality)) {
      this.postFx?.dispose();
      this.postFx = new PostFx(this.renderer, this.scene, this.camera, prefs.quality);
    }

    this.onResize();
  }

  /**
   * Collapse the camera easing so a jump lands this frame. Used by minimap clicks, F-to-Core,
   * and alert clicks — deliberate teleports, where easing would read as lag.
   */
  /**
   * Route a hit flash to whichever bespoke rig collection owns this entity. A squad appears in
   * exactly one of these, but checking all of them is a handful of Map lookups and avoids
   * duplicating the "which rig kind is this def" dispatch that already exists further down.
   */
  /** Live position of a projectile's target, for the render-side arc. */
  private projectileTargetPos(pr: Projectile): Vec2 | null {
    const st = this.sim.state;
    switch (pr.targetKind) {
      case 'squad':
        return st.squads.get(pr.targetId)?.pos ?? null;
      case 'worker':
        return st.workers.get(pr.targetId)?.pos ?? null;
      case 'building':
        return st.buildings.get(pr.targetId)?.pos ?? null;
      case 'mine':
        return st.mines.get(pr.targetId)?.pos ?? null;
      default:
        return null;
    }
  }

  private applyFlashToRigs(id: EntId, amount: number): void {
    const boss = this.bossRigs.get(id);
    if (boss) applyRigFlash(boss.group, amount);
    const single = this.ballistaMeshes.get(id) ?? this.tankMeshes.get(id) ?? this.fortressRamMeshes.get(id);
    if (single) applyRigFlash(single, amount);
    for (const group of this.mountMeshes.get(id) ?? []) applyRigFlash(group, amount);
    for (const group of this.beastMeshes.get(id) ?? []) applyRigFlash(group, amount);
    for (const rig of this.verdantSquadRigs.get(id) ?? []) applyRigFlash(rig.group, amount);
    for (const rig of this.ironSquadRigs.get(id) ?? []) applyRigFlash(rig.group, amount);
    for (const rig of this.factionSquadRigs.get(id) ?? []) applyRigFlash(rig.group, amount);
  }

  snapCameraToTarget(): void {
    this.camSmooth.x = this.camTarget.x;
    this.camSmooth.y = this.camTarget.y;
    this.zoomSmooth = this.zoom;
  }

  private onResize = (): void => {
    const w = this.container.clientWidth;
    const h = this.container.clientHeight;
    this.renderer.setSize(w, h);
    this.camera.aspect = w / h;
    this.camera.updateProjectionMatrix();
    // After renderer.setSize/setPixelRatio, so the composer picks up the current ratio.
    this.postFx?.setSize(w, h);
  };

  // ---------------------------------------------------------------- static world

  private buildStaticWorld(): void {
    const map = this.sim.map;
    this.groundTexture = makeGroundTexture(map.width, map.height);
    // The colour map is one 2048px canvas over a 270x180 m map (~7.6 texels/m), so on its own the
    // ground is soft and perfectly flat at close zoom. Layer heavily-tiled detail normal and
    // roughness maps over it: three gives each texture slot its own UV transform, so the large
    // albedo and the repeated detail coexist without a custom shader or a second UV set.
    this.groundDetail = makeGroundDetailMaps();
    const detailRepeatX = Math.max(1, Math.round(map.width / GROUND_DETAIL_METRES));
    const detailRepeatY = Math.max(1, Math.round(map.height / GROUND_DETAIL_METRES));
    this.groundDetail.normalMap.repeat.set(detailRepeatX, detailRepeatY);
    this.groundDetail.roughnessMap.repeat.set(detailRepeatX, detailRepeatY);
    const ground = new THREE.Mesh(
      new THREE.PlaneGeometry(map.width, map.height),
      new THREE.MeshStandardMaterial({
        map: this.groundTexture,
        normalMap: this.groundDetail.normalMap,
        normalScale: new THREE.Vector2(0.55, 0.55), // TEST
        roughnessMap: this.groundDetail.roughnessMap,
        roughness: 1,
        envMapIntensity: 0.35,
      }),
    );
    ground.rotation.x = -Math.PI / 2;
    ground.position.set(map.width / 2, -0.02, map.height / 2);
    ground.receiveShadow = true;
    this.scene.add(ground);

    // rim so the map edge doesn't fall into the void
    const rim = new THREE.Mesh(
      new THREE.PlaneGeometry(map.width * 3, map.height * 3),
      new THREE.MeshStandardMaterial({ color: 0x1c231c, roughness: 1 }),
    );
    rim.rotation.x = -Math.PI / 2;
    rim.position.set(map.width / 2, -0.4, map.height / 2);
    rim.receiveShadow = true;
    this.scene.add(rim);

    // Mountain walls: upright overlapping crags, rolling shoulders, and talus boulders.
    // Each formation stays inside the same ellipse navigation blocks, so its silhouette
    // communicates collision without long rotated meshes poking into the walkable lane.
    for (let i = 0; i < this.sim.map.rocks.length; i++) {
      this.scene.add(makeRockFormation(this.sim.map.rocks[i], i));
    }

    let rockSeed = 7;
    const rnd = () => {
      rockSeed = (rockSeed * 16807) % 2147483647;
      return rockSeed / 2147483647;
    };

    // grass tufts: one static instanced mesh, seeded scatter avoiding the lane and rocks
    {
      const tuftGeo = new THREE.ConeGeometry(0.09, 0.55, 4);
      tuftGeo.translate(0, 0.24, 0);
      const tuftMat = new THREE.MeshStandardMaterial({ roughness: 1 });
      const TUFTS = 1800;
      const tufts = new THREE.InstancedMesh(tuftGeo, tuftMat, TUFTS);
      const tuftColors = [new THREE.Color(0x55683f), new THREE.Color(0x5f7547), new THREE.Color(0x49593a)];
      const mat4 = new THREE.Matrix4();
      const quat = new THREE.Quaternion();
      const eul = new THREE.Euler();
      let placed = 0;
      let guard = 0;
      while (placed < TUFTS && guard++ < TUFTS * 6) {
        const x = rnd() * map.width;
        const z = rnd() * map.height;
        if (Math.abs(z - map.height / 2) < 9 && rnd() < 0.8) continue; // thin out the war lane
        let inRock = false;
        for (const r of this.sim.map.rocks) {
          if (Math.abs(x - r.pos.x) < r.w / 2 + 1 && Math.abs(z - r.pos.y) < r.h / 2 + 1) {
            inRock = true;
            break;
          }
        }
        if (inRock) continue;
        eul.set((rnd() - 0.5) * 0.5, rnd() * Math.PI, (rnd() - 0.5) * 0.5);
        quat.setFromEuler(eul);
        const s = 0.7 + rnd() * 0.9;
        mat4.compose(new THREE.Vector3(x, 0, z), quat, new THREE.Vector3(s, s * (0.7 + rnd() * 0.8), s));
        tufts.setMatrixAt(placed, mat4);
        tufts.setColorAt(placed, tuftColors[(rnd() * tuftColors.length) | 0]);
        placed++;
      }
      tufts.count = placed;
      tufts.frustumCulled = false;
      this.scene.add(tufts);
    }

    // Imported CC0 crystal clusters; root scaling still communicates remaining resources.
    for (const d of this.sim.state.deposits.values()) {
      const g = new THREE.Group();
      g.position.set(d.pos.x, 0, d.pos.y);
      g.name = `deposit-${d.id}`;
      this.scene.add(g);
      populateDepositModel(g, d.type, d.id);
    }

    // core territory rings
    for (const p of this.sim.playerIds()) {
      const c = map.corePositions[p];
      const ring = new THREE.Mesh(
        makeMapClippedRingGeometry(
          c,
          map.coreTerritoryRadius - 0.4,
          map.coreTerritoryRadius,
          map.width,
          map.height,
          96,
        ),
        new THREE.MeshBasicMaterial({
          color: OWNER_COLORS[p],
          transparent: true,
          opacity: 0.22,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      );
      ring.position.y = 0.02;
      this.scene.add(ring);
    }

    // sites: ring + progress disc + small neutral obelisk (replaced visually by the tower when owned)
    for (const site of this.sim.state.sites) {
      const ring = new THREE.Mesh(
        new THREE.RingGeometry(TUNING.captureRadius - 0.5, TUNING.captureRadius, 48),
        new THREE.MeshBasicMaterial({
          color: 0x9aa0a8,
          transparent: true,
          opacity: 0.6,
          depthWrite: false,
          side: THREE.DoubleSide,
        }),
      );
      ring.rotation.x = -Math.PI / 2;
      ring.position.set(site.pos.x, 0.03, site.pos.y);
      this.scene.add(ring);
      this.siteRings.push(ring);

      const disc = new THREE.Mesh(
        new THREE.CircleGeometry(TUNING.captureRadius - 1, 40),
        new THREE.MeshBasicMaterial({ color: 0xffffff, transparent: true, opacity: 0.16, depthWrite: false }),
      );
      disc.rotation.x = -Math.PI / 2;
      disc.position.set(site.pos.x, 0.025, site.pos.y);
      disc.scale.setScalar(0.001);
      this.scene.add(disc);
      this.siteDiscs.push(disc);

      const obelisk = new THREE.Group();
      const stoneMat = new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.85 });
      const base = new THREE.Mesh(new THREE.CylinderGeometry(1.4, 1.7, 0.5, 8), stoneMat);
      base.position.y = 0.25;
      base.castShadow = true;
      obelisk.add(base);
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(0.35, 0.5, 2.4, 6), stoneMat);
      shaft.position.y = 1.6;
      shaft.castShadow = true;
      obelisk.add(shaft);
      obelisk.position.set(site.pos.x, 0, site.pos.y);
      this.scene.add(obelisk);
      this.siteObelisks.push(obelisk);
    }

    // Imported CC0 castle rubble (hidden while a wall stands on it).
    for (const ruin of this.sim.state.ruins) {
      const g = new THREE.Group();
      g.position.set(ruin.pos.x, 0, ruin.pos.y);
      if (ruin.rot === 1) g.rotation.y = Math.PI / 2; // ring segments running east–west
      this.scene.add(g);
      this.ruinMeshes.set(ruin.id, g);
      populateRubbleModel(g, ruin.id);
    }
  }

  // ---------------------------------------------------------------- building factories

  static makeBuildingGroup(b: Building): THREE.Group {
    const bd = buildingDef(b.defId);
    const ownerColor = OWNER_COLORS[b.owner];
    const g = new THREE.Group();
    const stoneMat = new THREE.MeshStandardMaterial({ color: STONE, roughness: 0.85 });
    const darkMat = new THREE.MeshStandardMaterial({ color: STONE_DARK, roughness: 0.9 });
    const accentMat = new THREE.MeshStandardMaterial({
      color: mixColor(0xffffff, ownerColor, 0.85),
      roughness: 0.6,
    });

    const bodyGroup = new THREE.Group();
    bodyGroup.name = 'body';

    // Gate-bearing walls need a real aperture and animated leaves. All four legacy bespoke
    // castle-wall rigs are intentionally solid, so gate segments take this renderer-owned path.
    if (bd.kind === 'wall' && b.gateSpan > 0) {
      bodyGroup.add(makeGateWallRig(b));
    } else if (BESPOKE_BUILDING_DEFS.has(b.defId)) {
      bodyGroup.add(makeBespokeBuildingRig(b.defId, b.owner as 0 | 1, bd.footprint));
    } else if (b.isCore && bd.faction === 'verdant-wilds') {
      // Verdant Wilds bastion = a colossal Spirit Tree (owner request)
      const barkMat = new THREE.MeshStandardMaterial({ color: 0x6b4a30, roughness: 1, flatShading: true });
      const leafDark = new THREE.MeshStandardMaterial({ color: 0x3f6b2c, roughness: 1, flatShading: true });
      const leafLight = new THREE.MeshStandardMaterial({ color: 0x5f8a3c, roughness: 1, flatShading: true });
      const R = Math.max(bd.footprint.w, bd.footprint.h) / 2;
      // buttress roots radiating from the base
      for (let i = 0; i < 7; i++) {
        const a = (i / 7) * Math.PI * 2;
        const root = new THREE.Mesh(new THREE.CylinderGeometry(0.3, 1.0, 3.4, 5), barkMat);
        root.position.set(Math.cos(a) * (R - 1.2), 0.6, Math.sin(a) * (R - 1.2));
        root.rotation.z = Math.cos(a) * 0.5;
        root.rotation.x = -Math.sin(a) * 0.5;
        bodyGroup.add(root);
      }
      // massive tapered trunk
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(1.8, 3.2, 9, 9), barkMat);
      trunk.position.y = 4.5;
      bodyGroup.add(trunk);
      // glowing heartwood — the living core (named 'orb' so it pulses like other cores)
      const orb = new THREE.Mesh(
        new THREE.IcosahedronGeometry(1.3, 1),
        new THREE.MeshStandardMaterial({
          color: ownerColor,
          emissive: ownerColor,
          emissiveIntensity: 1.6,
          roughness: 0.3,
        }),
      );
      orb.name = 'orb';
      orb.position.y = 5.4;
      bodyGroup.add(orb);
      // broad layered canopy
      const canopy: [number, number, number, number][] = [
        [0, 11.5, 0, 4.2],
        [-3.2, 10, 1.4, 2.8],
        [3.0, 10.2, -1.2, 2.9],
        [1.2, 9, 3.2, 2.6],
        [-1.6, 9.2, -3.0, 2.6],
        [0, 13.4, 0.4, 2.6],
      ];
      for (let i = 0; i < canopy.length; i++) {
        const [x, y, z, r] = canopy[i];
        bodyGroup.add(leafClump(i % 2 === 0 ? leafDark : leafLight, x, y, z, r));
      }
      // ---- Core Annex System sockets (Heartroot): named `annex-<upId>` groups the update
      // loop reveals from b.upgradesDone / b.upgradeInProgress. top = canopy crown,
      // left = -x root cellar at ground, bottom = +z seed-pod stable at ground.
      const barkLight = new THREE.MeshStandardMaterial({ color: 0x8a6640, roughness: 1, flatShading: true });
      const petalMat = new THREE.MeshStandardMaterial({ color: 0xc86d94, roughness: 0.85, flatShading: true });
      const nectarMat = new THREE.MeshStandardMaterial({ color: 0xffd66d, emissive: 0xffb84d, emissiveIntensity: 1.1, roughness: 0.3, flatShading: true });
      {
        // top socket — Rootward Nursery: giant blossom crown + two hanging cocoon pods
        const annex = new THREE.Group();
        annex.name = 'annex-up-vw-rootward-nursery';
        const crown = new THREE.Group();
        crown.position.y = 14.6;
        const heartBud = new THREE.Mesh(new THREE.SphereGeometry(0.85, 9, 7), nectarMat);
        crown.add(heartBud);
        for (let i = 0; i < 8; i++) {
          const a = (i / 8) * Math.PI * 2;
          const petal = new THREE.Mesh(new THREE.SphereGeometry(0.8, 8, 6), petalMat);
          petal.scale.set(1.6, 0.35, 0.8);
          petal.position.set(Math.cos(a) * 1.15, 0.05, Math.sin(a) * 1.15);
          petal.rotation.y = -a;
          crown.add(petal);
        }
        annex.add(crown);
        for (const [px, py, pz] of [[-2.6, 12.4, 1.6], [2.8, 12.8, -1.0], [0.6, 13.6, 2.6]] as const) {
          const bud = new THREE.Mesh(new THREE.SphereGeometry(0.55, 8, 6), petalMat);
          bud.scale.y = 0.5;
          bud.position.set(px, py, pz);
          annex.add(bud);
          const budCore = new THREE.Mesh(new THREE.SphereGeometry(0.26, 7, 6), nectarMat);
          budCore.position.set(px, py + 0.22, pz);
          annex.add(budCore);
        }
        for (const [px, pz] of [[-2.3, 1.5], [2.5, -0.6]] as const) {
          const stem = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.08, 1.7, 5), barkMat);
          stem.position.set(px, 9.6, pz);
          annex.add(stem);
          const pod = new THREE.Mesh(new THREE.CapsuleGeometry(0.55, 0.8, 5, 8), barkLight);
          pod.position.set(px, 8.2, pz);
          annex.add(pod);
          const band = new THREE.Mesh(new THREE.TorusGeometry(0.56, 0.06, 5, 12), barkMat);
          band.rotation.x = Math.PI / 2;
          band.position.set(px, 8.3, pz);
          annex.add(band);
        }
        annex.visible = false;
        bodyGroup.add(annex);
      }
      {
        // left socket — Deeproot Sanctuary: broad root cellar with glowing tunnel windows
        const annex = new THREE.Group();
        annex.name = 'annex-up-vw-deeproot-sanctuary';
        const cellar = new THREE.Mesh(new THREE.SphereGeometry(2.1, 10, 7), barkMat);
        cellar.scale.set(0.95, 0.5, 1.35);
        cellar.position.set(-3.9, 0.5, 0);
        annex.add(cellar);
        const mossCap = new THREE.Mesh(new THREE.SphereGeometry(1.7, 9, 6), leafDark);
        mossCap.scale.set(0.95, 0.25, 1.25);
        mossCap.position.set(-3.9, 1.35, 0);
        annex.add(mossCap);
        for (const [rz, rr] of [[-1.6, 0.5], [0.2, -0.2], [1.5, 0.35]] as const) {
          const root = new THREE.Mesh(new THREE.CylinderGeometry(0.18, 0.42, 2.6, 6), barkLight);
          root.position.set(-2.4, 0.75, rz);
          root.rotation.z = 0.95;
          root.rotation.x = rr;
          annex.add(root);
        }
        for (const [wx, wz, wa] of [[-5.2, -1.4, -2.4], [-5.7, 0.3, -1.65], [-5.0, 1.7, -0.9]] as const) {
          const glow = new THREE.Mesh(
            new THREE.CircleGeometry(0.3, 8),
            new THREE.MeshStandardMaterial({ color: 0xffc86d, emissive: 0xffa64d, emissiveIntensity: 1.4, side: THREE.DoubleSide }),
          );
          glow.position.set(wx, 0.45, wz);
          glow.rotation.y = wa;
          annex.add(glow);
          const arch = new THREE.Mesh(new THREE.TorusGeometry(0.36, 0.07, 5, 10, Math.PI), barkLight);
          arch.position.set(wx, 0.45, wz);
          arch.rotation.y = wa;
          annex.add(arch);
        }
        annex.visible = false;
        bodyGroup.add(annex);
      }
      {
        // bottom socket — Seedcaravan Bower: enormous travel-ready seed pods out front
        const annex = new THREE.Group();
        annex.name = 'annex-up-vw-seedcaravan-bower';
        const bed = new THREE.Mesh(new THREE.CylinderGeometry(2.2, 2.45, 0.28, 10), leafDark);
        bed.position.set(0.2, 0.14, 3.8);
        annex.add(bed);
        for (const [px, py, pz, pr] of [[-1.2, 1.15, 3.4, 0.95], [0.8, 1.3, 4.4, 1.05], [1.9, 0.95, 3.2, 0.8]] as const) {
          const pod = new THREE.Mesh(new THREE.SphereGeometry(pr, 9, 7), barkLight);
          pod.scale.set(0.78, 1.2, 0.78);
          pod.position.set(px, py, pz);
          annex.add(pod);
          const sprout = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.6, 5), leafLight);
          sprout.position.set(px, py + pr * 1.2 + 0.24, pz);
          annex.add(sprout);
          const strap = new THREE.Mesh(new THREE.TorusGeometry(pr * 0.8, 0.06, 5, 12), barkMat);
          strap.rotation.x = Math.PI / 2;
          strap.position.set(px, py + 0.1, pz);
          annex.add(strap);
        }
        for (let i = 0; i < 4; i++) {
          const hoop = new THREE.Mesh(new THREE.TorusGeometry(0.5, 0.07, 5, 10, Math.PI), barkMat);
          hoop.position.set(-1.9 + i * 1.35, 0.26, 5.4);
          annex.add(hoop);
        }
        annex.visible = false;
        bodyGroup.add(annex);
      }
    } else if (b.isCore) {
      // walled keep with corner towers, banner, rotating ballista
      const base = new THREE.Mesh(new THREE.BoxGeometry(bd.footprint.w, 3.2, bd.footprint.h), stoneMat);
      base.position.y = 1.6;
      bodyGroup.add(base);
      for (const [cx, cz] of [
        [-1, -1],
        [-1, 1],
        [1, -1],
        [1, 1],
      ]) {
        const tower = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.3, 6, 8), darkMat);
        tower.position.set(cx * (bd.footprint.w / 2 - 0.6), 3, cz * (bd.footprint.h / 2 - 0.6));
        bodyGroup.add(tower);
        const cap = new THREE.Mesh(new THREE.ConeGeometry(1.3, 1.2, 8), accentMat);
        cap.position.set(cx * (bd.footprint.w / 2 - 0.6), 6.6, cz * (bd.footprint.h / 2 - 0.6));
        bodyGroup.add(cap);
      }
      const keep = new THREE.Mesh(new THREE.BoxGeometry(4.6, 5.5, 4.6), stoneMat);
      keep.position.y = 4;
      bodyGroup.add(keep);
      const keepRoof = new THREE.Mesh(new THREE.ConeGeometry(3.4, 2.2, 4), accentMat);
      keepRoof.position.y = 7.8;
      keepRoof.rotation.y = Math.PI / 4;
      bodyGroup.add(keepRoof);
      // ballista turret
      const turret = new THREE.Group();
      turret.name = 'turret';
      const mount = new THREE.Mesh(new THREE.CylinderGeometry(0.9, 1.1, 0.8, 8), darkMat);
      turret.add(mount);
      const arm = new THREE.Mesh(
        new THREE.BoxGeometry(0.6, 0.5, 3.6),
        new THREE.MeshStandardMaterial({ color: 0x3d4148, metalness: 0.5, roughness: 0.4 }),
      );
      arm.position.set(0, 0.6, -0.8);
      turret.add(arm);
      turret.position.y = 9.2;
      bodyGroup.add(turret);
      // banner
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.08, 0.08, 3.4, 5), darkMat);
      pole.position.set(0, 10.8, 1.8);
      bodyGroup.add(pole);
      const banner = new THREE.Mesh(
        new THREE.PlaneGeometry(1.6, 1.0),
        new THREE.MeshBasicMaterial({ color: ownerColor, side: THREE.DoubleSide }),
      );
      banner.position.set(0.85, 11.6, 1.8);
      bodyGroup.add(banner);

      // ---- annex decorations, revealed as Bastion upgrades finish (obvious visual change)
      const goldMat = new THREE.MeshStandardMaterial({ color: 0xc7a24f, metalness: 0.7, roughness: 0.35 });
      // Reinforced Bastion: outer buttress ring + gilded tower caps
      {
        const annex = new THREE.Group();
        annex.name = 'annex-up-ic-reinforced-bastion';
        for (let i = 0; i < 8; i++) {
          const ang = (i / 8) * Math.PI * 2;
          const but = new THREE.Mesh(new THREE.BoxGeometry(1.0, 2.4, 1.0), darkMat);
          but.position.set(Math.cos(ang) * (bd.footprint.w / 2 + 0.9), 1.2, Math.sin(ang) * (bd.footprint.h / 2 + 0.9));
          but.rotation.y = -ang;
          annex.add(but);
        }
        const crest = new THREE.Mesh(new THREE.TorusGeometry(3.3, 0.18, 6, 20), goldMat);
        crest.rotation.x = Math.PI / 2;
        crest.position.y = 6.9;
        annex.add(crest);
        annex.visible = false;
        bodyGroup.add(annex);
      }
      // Royal Workshop: side hut with chimney and anvil-glow window
      {
        const annex = new THREE.Group();
        annex.name = 'annex-up-ic-royal-workshop';
        const hut = new THREE.Mesh(new THREE.BoxGeometry(2.6, 2.0, 2.2), stoneMat);
        hut.position.set(bd.footprint.w / 2 + 1.8, 1.0, -1.4);
        annex.add(hut);
        const roof = new THREE.Mesh(new THREE.ConeGeometry(2.0, 1.2, 4), new THREE.MeshStandardMaterial({ color: ROOF, roughness: 0.9 }));
        roof.position.set(bd.footprint.w / 2 + 1.8, 2.6, -1.4);
        roof.rotation.y = Math.PI / 4;
        annex.add(roof);
        const chimney = new THREE.Mesh(new THREE.CylinderGeometry(0.22, 0.28, 1.6, 6), darkMat);
        chimney.position.set(bd.footprint.w / 2 + 2.5, 3.2, -0.8);
        annex.add(chimney);
        const glow = new THREE.Mesh(
          new THREE.PlaneGeometry(0.8, 0.6),
          new THREE.MeshBasicMaterial({ color: 0xff9c3f }),
        );
        glow.position.set(bd.footprint.w / 2 + 0.48, 0.9, -1.4);
        glow.rotation.y = -Math.PI / 2;
        annex.add(glow);
        annex.visible = false;
        bodyGroup.add(annex);
      }
      // Command Hall: tall standard spire with three banners
      {
        const annex = new THREE.Group();
        annex.name = 'annex-up-ic-command-hall';
        const spire = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.22, 6.5, 6), goldMat);
        spire.position.set(-bd.footprint.w / 2 - 1.2, 3.2, 1.6);
        annex.add(spire);
        for (let i = 0; i < 3; i++) {
          const bn = new THREE.Mesh(
            new THREE.PlaneGeometry(1.2, 0.55),
            new THREE.MeshBasicMaterial({ color: ownerColor, side: THREE.DoubleSide }),
          );
          bn.position.set(-bd.footprint.w / 2 - 0.55, 6.0 - i * 1.0, 1.6);
          annex.add(bn);
        }
        annex.visible = false;
        bodyGroup.add(annex);
      }
    } else if (b.defId === 'b-vw-site-tower') {
      // Verdant Wilds site tower = a watch-tree (owner request)
      const barkMat = new THREE.MeshStandardMaterial({ color: 0x6b4a30, roughness: 1, flatShading: true });
      const leafDark = new THREE.MeshStandardMaterial({ color: 0x3f6b2c, roughness: 1, flatShading: true });
      const leafLight = new THREE.MeshStandardMaterial({ color: 0x5f8a3c, roughness: 1, flatShading: true });
      // root flare + trunk
      const flare = new THREE.Mesh(new THREE.CylinderGeometry(1.1, 1.9, 1.0, 7), barkMat);
      flare.position.y = 0.5;
      bodyGroup.add(flare);
      const trunk = new THREE.Mesh(new THREE.CylinderGeometry(0.7, 1.1, 5.6, 7), barkMat);
      trunk.position.y = 3.6;
      bodyGroup.add(trunk);
      // glowing spirit-fruit (named 'orb' to reuse the pulse + armed-gold logic)
      const orb = new THREE.Mesh(
        new THREE.IcosahedronGeometry(0.85, 1),
        new THREE.MeshStandardMaterial({ color: ownerColor, emissive: ownerColor, emissiveIntensity: 1.6, roughness: 0.3 }),
      );
      orb.name = 'orb';
      orb.position.y = 6.4;
      bodyGroup.add(orb);
      // canopy clumps
      const canopy: [number, number, number, number][] = [
        [0, 7.4, 0, 2.4],
        [-1.7, 6.4, 0.7, 1.6],
        [1.6, 6.6, -0.6, 1.6],
        [0.3, 8.6, 0.2, 1.5],
      ];
      for (let i = 0; i < canopy.length; i++) {
        const [x, y, z, r] = canopy[i];
        bodyGroup.add(leafClump(i % 2 === 0 ? leafDark : leafLight, x, y, z, r));
      }
      // thorn-launcher armament: a bramble ballista that snaps out once armed (named
      // 'arbalest' so the existing upgrade-visibility + target-tracking logic drives it)
      const arb = new THREE.Group();
      arb.name = 'arbalest';
      const thornMat = new THREE.MeshStandardMaterial({ color: 0x4a3a26, roughness: 0.9, flatShading: true });
      const barb = new THREE.Mesh(new THREE.ConeGeometry(0.22, 2.4, 6), new THREE.MeshStandardMaterial({ color: 0xbfe08a, emissive: 0x6f9c3f, emissiveIntensity: 0.5 }));
      barb.rotation.x = -Math.PI / 2;
      barb.position.set(0, 0.3, -1.4);
      arb.add(barb);
      for (const side of [-1, 1]) {
        const branch = new THREE.Mesh(new THREE.CylinderGeometry(0.12, 0.18, 1.8, 5), thornMat);
        branch.position.set(side * 0.6, 0.3, -0.9);
        branch.rotation.z = side * 0.6;
        branch.rotation.x = Math.PI / 2.4;
        arb.add(branch);
      }
      arb.position.y = 5.6;
      arb.visible = false;
      bodyGroup.add(arb);
    } else if (b.defId === 'b-ic-site-tower') {
      const base = new THREE.Mesh(new THREE.CylinderGeometry(1.7, 2.1, 1.0, 8), darkMat);
      base.position.y = 0.5;
      bodyGroup.add(base);
      const shaft = new THREE.Mesh(new THREE.CylinderGeometry(1.05, 1.45, 5.4, 8), stoneMat);
      shaft.position.y = 3.4;
      bodyGroup.add(shaft);
      const crown = new THREE.Mesh(new THREE.CylinderGeometry(1.5, 1.2, 0.9, 8), darkMat);
      crown.position.y = 6.4;
      bodyGroup.add(crown);
      // battlement teeth
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const tooth = new THREE.Mesh(new THREE.BoxGeometry(0.4, 0.5, 0.35), stoneMat);
        tooth.position.set(Math.cos(a) * 1.35, 7.1, Math.sin(a) * 1.35);
        tooth.rotation.y = -a;
        bodyGroup.add(tooth);
      }
      // the glowing sphere
      const orb = new THREE.Mesh(
        new THREE.SphereGeometry(0.95, 16, 12),
        new THREE.MeshStandardMaterial({
          color: ownerColor,
          emissive: ownerColor,
          emissiveIntensity: 1.6,
          roughness: 0.2,
        }),
      );
      orb.name = 'orb';
      orb.position.y = 8.3;
      bodyGroup.add(orb);
      // heavy twin-arbalest turret — appears (and reads unmistakably) once the tower is armed
      const arb = new THREE.Group();
      arb.name = 'arbalest';
      const armMat = new THREE.MeshStandardMaterial({ color: 0x3d4148, metalness: 0.6, roughness: 0.35 });
      const goldTrim = new THREE.MeshStandardMaterial({ color: 0xc7a24f, metalness: 0.75, roughness: 0.3 });
      // gilded rotating mount ring
      const mountRing = new THREE.Mesh(new THREE.CylinderGeometry(0.95, 1.1, 0.4, 10), goldTrim);
      mountRing.position.y = -0.1;
      arb.add(mountRing);
      const stock = new THREE.Mesh(new THREE.BoxGeometry(0.5, 0.4, 2.8), armMat);
      stock.position.z = -0.5;
      stock.position.y = 0.25;
      arb.add(stock);
      // two bow limbs angled forward like a drawn crossbow
      for (const side of [-1, 1]) {
        const limb = new THREE.Mesh(new THREE.BoxGeometry(1.7, 0.22, 0.28), armMat);
        limb.position.set(side * 0.85, 0.4, -1.3);
        limb.rotation.y = side * 0.5;
        arb.add(limb);
        const tip = new THREE.Mesh(new THREE.ConeGeometry(0.14, 0.4, 6), goldTrim);
        tip.position.set(side * 1.55, 0.4, -1.75);
        tip.rotation.x = -Math.PI / 2;
        arb.add(tip);
      }
      // bowstring + a loaded bolt jutting forward (bright tip so the armament pops)
      const string = new THREE.Mesh(new THREE.BoxGeometry(2.9, 0.06, 0.06), new THREE.MeshStandardMaterial({ color: 0x1c1c1c }));
      string.position.set(0, 0.4, -1.05);
      arb.add(string);
      const bolt = new THREE.Mesh(new THREE.CylinderGeometry(0.09, 0.09, 2.4, 6), armMat);
      bolt.rotation.x = Math.PI / 2;
      bolt.position.set(0, 0.4, -1.4);
      arb.add(bolt);
      const boltHead = new THREE.Mesh(new THREE.ConeGeometry(0.16, 0.5, 6), new THREE.MeshStandardMaterial({ color: 0xffcf5e, emissive: 0xff9c3f, emissiveIntensity: 0.6 }));
      boltHead.rotation.x = Math.PI / 2;
      boltHead.position.set(0, 0.4, -2.7);
      arb.add(boltHead);
      arb.position.y = 6.9;
      arb.visible = false;
      bodyGroup.add(arb);
      // armored battlement caps that snap on with the upgrade (gilded merlon crowns)
      const armCaps = new THREE.Group();
      armCaps.name = 'towerArmor';
      for (let i = 0; i < 6; i++) {
        const a = (i / 6) * Math.PI * 2;
        const cap = new THREE.Mesh(new THREE.ConeGeometry(0.26, 0.6, 5), goldTrim);
        cap.position.set(Math.cos(a) * 1.35, 7.55, Math.sin(a) * 1.35);
        armCaps.add(cap);
      }
      armCaps.visible = false;
      bodyGroup.add(armCaps);
    } else if (b.defId === 'b-vw-castle-wall') {
      // Rootwall: living cables interlock in distinct courses instead of masquerading as stone.
      bodyGroup.userData.layeredGrowth = true;
      const w = bd.footprint.w;
      const h = bd.footprint.h;
      const span = b.gateSpan > 0 ? Math.min(b.gateSpan, h - 2) : 0;
      const rootMat = new THREE.MeshStandardMaterial({ color: 0x68482d, roughness: 1, flatShading: true });
      const rootLight = new THREE.MeshStandardMaterial({ color: 0x8a6640, roughness: 1, flatShading: true });
      const thornMat = new THREE.MeshStandardMaterial({ color: 0x758a4b, roughness: 0.9, flatShading: true });
      for (let layer = 0; layer < 6; layer++) {
        const course = new THREE.Group();
        course.name = `growthLayer${layer}`;
        course.position.y = 0.42 + layer * 0.62;
        const segments = 9;
        for (let i = 0; i < segments; i++) {
          const z = -h / 2 + (i + 0.5) * (h / segments);
          if (span > 0 && Math.abs(z) < span / 2) continue;
          const cable = new THREE.Mesh(new THREE.CylinderGeometry(0.11, 0.15, h / segments + 0.28, 6), (i + layer) % 2 ? rootMat : rootLight);
          cable.rotation.x = Math.PI / 2;
          cable.rotation.z = ((i + layer) % 3 - 1) * 0.12;
          cable.position.set(((i + layer) % 2 ? -0.22 : 0.22), 0, z);
          course.add(cable);
          if ((i + layer) % 2 === 0) {
            const thorn = new THREE.Mesh(new THREE.ConeGeometry(0.08, 0.32, 5), thornMat);
            thorn.position.set(layer % 2 ? -w * 0.42 : w * 0.42, 0.18, z);
            thorn.rotation.z = layer % 2 ? 1.0 : -1.0;
            course.add(thorn);
          }
        }
        // Cross-laced roots bind each new course to the previous one.
        for (let i = 0; i < 7; i++) {
          const z = -h / 2 + 0.8 + i * ((h - 1.6) / 6);
          if (span > 0 && Math.abs(z) < span / 2) continue;
          const bind = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.1, w * 1.2, 5), rootLight);
          bind.rotation.z = Math.PI / 2;
          bind.rotation.x = (i % 2 ? 1 : -1) * 0.28;
          bind.position.set(0, 0.08, z);
          course.add(bind);
        }
        bodyGroup.add(course);
      }
      // Massive end-root columns anchor the woven barricade.
      const anchors = new THREE.Group();
      anchors.name = 'rootAnchors';
      for (const z of [-h / 2, h / 2]) {
        const anchor = new THREE.Mesh(new THREE.CylinderGeometry(0.58, 0.88, 4.5, 7), rootMat);
        anchor.position.set(0, 2.25, z);
        anchors.add(anchor);
        for (let i = 0; i < 4; i++) {
          const crown = new THREE.Mesh(new THREE.ConeGeometry(0.12, 0.7, 5), thornMat);
          crown.position.set((i - 1.5) * 0.22, 4.65, z);
          anchors.add(crown);
        }
      }
      bodyGroup.add(anchors);
    } else if (bd.kind === 'wall') {
      const w = bd.footprint.w;
      const h = bd.footprint.h;
      const span = b.gateSpan > 0 ? Math.min(b.gateSpan, h - 2) : 0;
      if (span > 0) {
        // two curtain segments flanking the gate, with a lintel arch above the opening
        const segH = (h - span) / 2;
        for (const zc of [-(span / 2 + segH / 2), span / 2 + segH / 2]) {
          const seg = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, 3.6, segH), stoneMat);
          seg.position.set(0, 1.8, zc);
          bodyGroup.add(seg);
        }
        const lintel = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, 1.0, span), stoneMat);
        lintel.position.set(0, 3.1, 0);
        bodyGroup.add(lintel);
      } else {
        const wall = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, 3.6, h), stoneMat);
        wall.position.y = 1.8;
        bodyGroup.add(wall);
      }
      const walk = new THREE.Mesh(new THREE.BoxGeometry(w + 1.2, 0.4, h), darkMat);
      walk.position.y = 3.8;
      bodyGroup.add(walk);
      // crenellations along the top
      for (let i = 0; i < 5; i++) {
        const z = -h / 2 + 1 + i * ((h - 2) / 4);
        const cren = new THREE.Mesh(new THREE.BoxGeometry(w + 0.6, 0.7, 0.7), stoneMat);
        cren.position.set(0, 4.35, z);
        bodyGroup.add(cren);
      }
      // end towers
      for (const z of [-h / 2, h / 2]) {
        const t = new THREE.Mesh(new THREE.CylinderGeometry(1.0, 1.2, 5.2, 8), darkMat);
        t.position.set(0, 2.6, z);
        bodyGroup.add(t);
        const cap = new THREE.Mesh(new THREE.ConeGeometry(1.2, 1.0, 8), accentMat);
        cap.position.set(0, 5.7, z);
        bodyGroup.add(cap);
      }
    } else {
      // barracks / generic production hall
      const hall = new THREE.Mesh(new THREE.BoxGeometry(bd.footprint.w, 2.6, bd.footprint.h), stoneMat);
      hall.position.y = 1.3;
      bodyGroup.add(hall);
      const roof = new THREE.Mesh(
        new THREE.CylinderGeometry(0.1, bd.footprint.w * 0.72, 2.0, 4),
        new THREE.MeshStandardMaterial({ color: ROOF, roughness: 0.9 }),
      );
      roof.position.y = 3.6;
      roof.rotation.y = Math.PI / 4;
      bodyGroup.add(roof);
      const door = new THREE.Mesh(
        new THREE.BoxGeometry(1.2, 1.6, 0.2),
        new THREE.MeshStandardMaterial({ color: 0x33261c, roughness: 1 }),
      );
      door.position.set(0, 0.8, bd.footprint.h / 2 + 0.05);
      bodyGroup.add(door);
      const pole = new THREE.Mesh(new THREE.CylinderGeometry(0.07, 0.07, 3.2, 5), darkMat);
      pole.position.set(bd.footprint.w / 2 - 0.4, 4.4, bd.footprint.h / 2 - 0.4);
      bodyGroup.add(pole);
      const banner = new THREE.Mesh(
        new THREE.PlaneGeometry(1.1, 0.7),
        new THREE.MeshBasicMaterial({ color: ownerColor, side: THREE.DoubleSide }),
      );
      banner.position.set(bd.footprint.w / 2 + 0.2, 5.5, bd.footprint.h / 2 - 0.4);
      bodyGroup.add(banner);
      // Veteran Drilling decoration: gilded roof ridge + drill-yard weapon racks
      {
        const deco = new THREE.Group();
        deco.name = 'upgradeDeco';
        const goldMat = new THREE.MeshStandardMaterial({ color: 0xc7a24f, metalness: 0.7, roughness: 0.35 });
        const ridge = new THREE.Mesh(new THREE.CylinderGeometry(0.14, 0.14, bd.footprint.w * 0.9, 6), goldMat);
        ridge.rotation.z = Math.PI / 2;
        ridge.position.y = 4.55;
        deco.add(ridge);
        for (const sx of [-1.2, 0, 1.2]) {
          const rackPole = new THREE.Mesh(new THREE.CylinderGeometry(0.06, 0.06, 1.3, 5), goldMat);
          rackPole.position.set(sx, 0.65, -bd.footprint.h / 2 - 0.8);
          deco.add(rackPole);
          const blade = new THREE.Mesh(new THREE.ConeGeometry(0.09, 0.7, 5), goldMat);
          blade.position.set(sx, 1.5, -bd.footprint.h / 2 - 0.8);
          deco.add(blade);
        }
        deco.visible = false;
        bodyGroup.add(deco);
      }
    }

    // rotated footprint (ring wall segments running east–west)
    if (b.rot === 1) bodyGroup.rotation.y = Math.PI / 2;

    g.add(bodyGroup);
    g.traverse((o) => {
      if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) {
        o.castShadow = true;
        o.receiveShadow = true;
      }
    });
    g.position.set(b.pos.x, 0, b.pos.y);
    return g;
  }

  /** Rumble kick with distance falloff from the camera focus. Applied to the camera in update(). */
  private addShakeAt(x: number, z: number, amt: number): void {
    if (!this.cameraShakeEnabled || this.reducedEffects) return;
    const dist = Math.hypot(x - this.camTarget.x, z - this.camTarget.y);
    this.shakeAmp = Math.min(1.2, this.shakeAmp + amt / (1 + (dist / 28) ** 2));
  }

  /** pure machine — leaves debris (particles), not a body or wreck */
  private static readonly NO_CORPSE = new Set(['u-ad-siege-orb', 'u-vw-sproutling-swarm']);
  /** machine units that leave a charred wreck instead of bodies */
  private static readonly WRECK_DEFS = new Set(['u-ic-ironclad-tank', 'u-ic-fortress-ram', 'boss-ad-war-engine']);

  private static corpseKindFor(defId: string): Corpse['kind'] {
    if (GameRenderer.WRECK_DEFS.has(defId)) return 'wreck';
    const d = unitDef(defId);
    if (d.sizeTag === 'giant' || defId.startsWith('boss-')) return 'giant';
    return 'body';
  }

  /** Drop a single fallen body/wreck at a world position (render-side only). */
  private pushCorpse(defId: string, owner: PlayerId, x: number, z: number, yaw: number): void {
    if (GameRenderer.NO_CORPSE.has(defId)) return;
    const d = unitDef(defId);
    const kind = GameRenderer.corpseKindFor(defId);
    const style = STYLES[defId];
    const baseColor = style ? style.bodyColors[owner] : mixColor(d.ui.color, OWNER_COLORS[owner], 0.2);
    // corpses read darker than the living unit
    const color = baseColor.clone().multiplyScalar(0.75);
    const isWorker = d.roleTags.includes('worker');
    const scale = kind === 'wreck' ? d.ui.scale * 1.2 : kind === 'giant' ? d.ui.scale * 1.6 : isWorker ? 0.55 : d.ui.scale; // TEST
    if (this.corpses.length >= MAX_CORPSES) this.corpses.shift();
    this.corpses.push({
      kind,
      x: x + (Math.random() - 0.5) * 0.3,
      z: z + (Math.random() - 0.5) * 0.3,
      yaw: kind === 'wreck' ? yaw + (Math.random() - 0.5) * 0.4 : yaw + Math.PI + (Math.random() - 0.5) * 1.4,
      scale,
      color,
      age: 0,
      fallDur: CORPSE_FALL_T * (0.8 + Math.random() * 0.5),
    });
  }

  /**
   * Drop bodies where a squad died. `remaining` limits to the members still alive at death
   * (earlier losses already dropped their own bodies mid-fight); giants and machines always
   * leave a single large body/wreck.
   */
  private spawnCorpses(defId: string, owner: PlayerId, pos: Vec2, entId: EntId, remaining?: number): void {
    if (GameRenderer.NO_CORPSE.has(defId)) return;
    const d = unitDef(defId);
    // fall roughly along the squad's last heading (smoothed render-side yaw if we have one)
    const baseYaw = this.anim.get(entId)?.yaw ?? this.bossAnim.get(entId)?.yaw ?? this.workerYaw.get(entId) ?? Math.random() * Math.PI * 2;
    if (GameRenderer.corpseKindFor(defId) !== 'body') {
      this.pushCorpse(defId, owner, pos.x, pos.y, baseYaw);
      return;
    }
    const isWorker = d.roleTags.includes('worker');
    const scale = isWorker ? 0.55 : d.ui.scale;
    const cos = Math.cos(baseYaw + Math.PI / 2);
    const sin = Math.sin(baseYaw + Math.PI / 2);
    const count = Math.min(remaining ?? d.squadMemberCount, d.squadMemberCount);
    const offsets = memberOffsets(d.squadMemberCount);
    for (let i = 0; i < count; i++) {
      const o = offsets[i] ?? { x: 0, y: 0 };
      this.pushCorpse(
        defId,
        owner,
        pos.x + (o.x * cos - o.y * sin) * scale,
        pos.y + (o.x * sin + o.y * cos) * scale,
        baseYaw,
      );
    }
  }

  // ---------------------------------------------------------------- picking

  screenToWorld(clientX: number, clientY: number): Vec2 | null {
    const rect = this.renderer.domElement.getBoundingClientRect();
    const ndc = new THREE.Vector2(
      ((clientX - rect.left) / rect.width) * 2 - 1,
      -((clientY - rect.top) / rect.height) * 2 + 1,
    );
    this.raycaster.setFromCamera(ndc, this.camera);
    const hit = new THREE.Vector3();
    if (this.raycaster.ray.intersectPlane(this.groundPlane, hit)) {
      return { x: hit.x, y: hit.z };
    }
    return null;
  }

  /**
   * The viewport's footprint on the ground plane, as four world-space corners in
   * top-left / top-right / bottom-right / bottom-left order. Used by the minimap to draw the
   * true camera view instead of a fixed-size rectangle.
   *
   * Returns null if any corner ray misses the ground plane — at a shallow enough pitch the top
   * corners point at the horizon, and a partial quad would be worse than none.
   */
  viewportGroundQuad(): [Vec2, Vec2, Vec2, Vec2] | null {
    const ndcCorners: [number, number][] = [[-1, 1], [1, 1], [1, -1], [-1, -1]];
    const out: Vec2[] = [];
    const hit = new THREE.Vector3();
    for (const [nx, ny] of ndcCorners) {
      this.raycaster.setFromCamera(new THREE.Vector2(nx, ny), this.camera);
      if (!this.raycaster.ray.intersectPlane(this.groundPlane, hit)) return null;
      out.push({ x: hit.x, y: hit.z });
    }
    return out as [Vec2, Vec2, Vec2, Vec2];
  }

  worldToScreen(p: Vec2): { x: number; y: number } {
    const v = new THREE.Vector3(p.x, 0, p.y).project(this.camera);
    const rect = this.renderer.domElement.getBoundingClientRect();
    return { x: ((v.x + 1) / 2) * rect.width + rect.left, y: ((1 - v.y) / 2) * rect.height + rect.top };
  }

  setGhost(pos: Vec2 | null, size: { w: number; h: number } | null, valid: boolean): void {
    if (!pos || !size) {
      this.ghostMesh.visible = false;
      return;
    }
    this.ghostMesh.visible = true;
    this.ghostMesh.scale.set(size.w, 2, size.h);
    this.ghostMesh.position.set(pos.x, 1, pos.y);
    (this.ghostMesh.material as THREE.MeshBasicMaterial).color.set(valid ? 0x44ff88 : 0xff5555);
  }

  /** Classic RTS click feedback: a contracting double-pulse ring at the order point. */
  spawnOrderMarker(pos: Vec2, kind: OrderMarkerKind): void {
    const color = ORDER_MARKER_COLORS[kind];
    this.fx.push({ pos, ttl: 0.45, maxTtl: 0.45, color, radius: 1.6, shrink: true });
    this.fx.push({ pos, ttl: 0.3, maxTtl: 0.3, color, radius: 1.0, shrink: true, delay: 0.12 });
    if (kind === 'attack') this.particles.burst(pos.x, pos.y, color);
  }

  private spawnFungalGroundVisual(x: number, z: number, healing: boolean): void {
    const group = new THREE.Group();
    group.position.set(x, 0, z);
    const disc = new THREE.Mesh(
      new THREE.CircleGeometry(healing ? 3.2 : 3.8, 32),
      new THREE.MeshBasicMaterial({
        color: healing ? 0x6f9f4d : 0x46334f,
        transparent: true,
        opacity: healing ? 0.2 : 0.27,
        depthWrite: false,
      }),
    );
    disc.rotation.x = -Math.PI / 2;
    disc.position.y = 0.035;
    (disc.material as THREE.Material).userData.baseOpacity = (disc.material as THREE.MeshBasicMaterial).opacity;
    group.add(disc);
    if (healing) {
      const positions = [[-1.7, -0.8], [-0.9, 1.4], [0.1, -1.3], [1.3, 0.8], [1.8, -1.1], [0.3, 1.7], [-1.5, 0.7]];
      positions.forEach(([px, pz], i) => {
        const moss = new THREE.Mesh(
          new THREE.IcosahedronGeometry(0.25 + (i % 3) * 0.07, 0),
          new THREE.MeshStandardMaterial({ color: i % 2 ? 0x78a858 : 0x91bd65, emissive: 0x456b35, emissiveIntensity: 0.45, roughness: 1 }),
        );
        moss.scale.y = 0.32;
        moss.position.set(px, 0.09, pz);
        group.add(moss);
        const stem = new THREE.Mesh(
          new THREE.CylinderGeometry(0.055, 0.075, 0.38 + (i % 2) * 0.16, 6),
          new THREE.MeshStandardMaterial({ color: 0xe0d8b7, roughness: 0.9 }),
        );
        stem.position.set(px, 0.23, pz);
        group.add(stem);
        const cap = new THREE.Mesh(
          new THREE.SphereGeometry(0.22 + (i % 2) * 0.07, 9, 6, 0, Math.PI * 2, 0, Math.PI / 2),
          new THREE.MeshStandardMaterial({ color: i % 3 === 0 ? 0xf1c66d : 0xb8e27c, emissive: 0x83bb55, emissiveIntensity: 0.75, roughness: 0.75 }),
        );
        cap.scale.y = 0.62;
        cap.position.set(px, 0.46 + (i % 2) * 0.14, pz);
        group.add(cap);
      });
    } else {
      for (let i = 0; i < 9; i++) {
        const a = (i / 9) * Math.PI * 2;
        const cloud = new THREE.Mesh(
          new THREE.IcosahedronGeometry(0.48 + (i % 3) * 0.16, 1),
          new THREE.MeshStandardMaterial({
            color: i % 2 ? 0x503858 : 0x3e3541,
            emissive: 0x241d2b,
            emissiveIntensity: 0.35,
            transparent: true,
            opacity: 0.34,
            depthWrite: false,
            roughness: 1,
          }),
        );
        cloud.position.set(Math.cos(a) * (1.25 + (i % 2) * 0.65), 0.55 + (i % 3) * 0.42, Math.sin(a) * 1.35);
        (cloud.material as THREE.Material).userData.baseOpacity = 0.34;
        group.add(cloud);
      }
    }
    group.scale.setScalar(0.08);
    this.scene.add(group);
    this.verdantGroundVisuals.push({ group, ttl: healing ? 4.2 : 3.2, maxTtl: healing ? 4.2 : 3.2 });
  }

  private spawnSolarLight(x: number, z: number): void {
    const group = new THREE.Group();
    group.position.set(x, 1.6, z);
    const light = new THREE.PointLight(0xffd86a, 3.2, 13, 2);
    light.userData.baseIntensity = 3.2;
    group.add(light);
    const corona = new THREE.Mesh(
      new THREE.SphereGeometry(0.45, 10, 8),
      new THREE.MeshBasicMaterial({ color: 0xffdf72, transparent: true, opacity: 0.42, depthWrite: false }),
    );
    (corona.material as THREE.Material).userData.baseOpacity = 0.42;
    group.add(corona);
    this.scene.add(group);
    this.verdantGroundVisuals.push({ group, ttl: 1.25, maxTtl: 1.25 });
  }

  addEvents(events: SimEvent[]): void {
    for (const e of events) {
      switch (e.type) {
        case 'ProjectileHit':
          this.fx.push({ pos: e.pos, ttl: 0.25, maxTtl: 0.25, color: 0xffd27f, radius: 1.1 });
          this.particles.hit(e.pos.x, e.pos.y);
          this.vfx.flash(e.pos.x, 1.5, e.pos.y, 0xffd9a0, 0.5);
          break;
        case 'SquadDied': {
          this.fx.push({ pos: e.pos, ttl: 0.6, maxTtl: 0.6, color: 0xcccccc, radius: 2.2 });
          if (e.defId === 'u-vw-sproutling-swarm') {
            const rigs = this.verdantSquadRigs.get(e.id);
            if (rigs && rigs.length > 0) {
              for (const rig of rigs) {
                rig.group.getWorldPosition(this.tmpPos);
                this.particles.sproutlingBurst(this.tmpPos.x, this.tmpPos.z);
              }
            } else {
              this.particles.sproutlingBurst(e.pos.x, e.pos.y, true);
            }
          } else {
            this.particles.death(e.pos.x, e.pos.y, OWNER_COLORS[e.owner], e.wasBoss);
          }
          if (GameRenderer.WRECK_DEFS.has(e.defId)) {
            this.particles.collapse(e.pos.x, e.pos.y); // smoke off the wreck
            this.vfx.smoke(e.pos.x, e.pos.y, 1.6);
            this.vfx.embers(e.pos.x, e.pos.y, 1.2);
            this.decals.add(e.pos.x, e.pos.y, SPRITE.scorch, 4.5, DECAL_SCORCH, 0.55, 0, -1);
          }
          // Bodies stain the ground they fall on. Bosses leave a much larger mark, and these
          // never fade — the battlefield accumulating a history of its fights is the same
          // intent as the persistent bone piles.
          this.decals.add(e.pos.x, e.pos.y, e.wasBoss ? SPRITE.crater : SPRITE.stain, e.wasBoss ? 5 : 2.2, DECAL_STAIN, e.wasBoss ? 0.4 : 0.28, 0, -1);
          // only the members still standing drop bodies here — earlier casualties already fell
          const remaining = this.anim.get(e.id)?.prevMembers;
          this.spawnCorpses(e.defId, e.owner, e.pos, e.id, remaining);
          if (e.wasBoss) this.addShakeAt(e.pos.x, e.pos.y, 0.7); // a giant falls
          break;
        }
        case 'WorkerDied':
          this.particles.death(e.pos.x, e.pos.y, OWNER_COLORS[e.owner]);
          this.spawnCorpses(e.defId, e.owner, e.pos, e.id);
          break;
        case 'GiantCrush':
          // sim-confirmed kill: the boss rig plays its hoist-and-crush flourish
          this.pendingCrush.add(e.giantId);
          this.particles.burst(e.pos.x, e.pos.y, 0xc94040);
          break;
        case 'BuildingDestroyed':
          this.fx.push({ pos: e.pos, ttl: 0.8, maxTtl: 0.8, color: 0xb0a894, radius: 4 });
          this.particles.collapse(e.pos.x, e.pos.y);
          this.addShakeAt(e.pos.x, e.pos.y, 0.5);
          this.vfx.shockwave(e.pos.x, e.pos.y, 0xd8c9a8, 7);
          this.vfx.dustPlume(e.pos.x, e.pos.y, 2.2);
          this.vfx.smoke(e.pos.x, e.pos.y, 2.4);
          this.vfx.embers(e.pos.x, e.pos.y, 1.5);
          this.decals.add(e.pos.x, e.pos.y, SPRITE.crater, 8, DECAL_SCORCH, 0.5, 0, -1);
          break;
        case 'AbilityTriggered':
          if (e.abilityId === 'ab-vw-thornback-charge') {
            this.fx.push({ pos: e.pos, ttl: 0.9, maxTtl: 0.9, color: 0x9aca5a, radius: 4.8 });
            this.particles.burst(e.pos.x, e.pos.y, 0x9aca5a, true);
            this.particles.dust(e.pos.x, e.pos.y, 1.5, [0x5f7a44, 0x8a9a5a, 0x9aca5a]);
            this.addShakeAt(e.pos.x, e.pos.y, 0.35);
          } else if (e.abilityId === 'ab-vw-pollen-pop') {
            this.verdantAbilityCues.set(e.id, { cue: 'healing', remaining: 1.2 });
            this.fx.push({ pos: e.pos, ttl: 0.8, maxTtl: 0.8, color: 0xe6d987, radius: 3.4 });
            this.particles.sproutlingBurst(e.pos.x, e.pos.y, true);
          } else if (e.abilityId === 'ab-vw-spore-cloud') {
            this.verdantAbilityCues.set(e.id, { cue: 'harmful', remaining: 1.6 });
            this.fx.push({ pos: e.pos, ttl: 1.1, maxTtl: 1.1, color: 0x5a3d6e, radius: 4.2 });
            this.particles.fungalBurst(e.pos.x, e.pos.y, false);
            this.spawnFungalGroundVisual(e.pos.x, e.pos.y, false);
          } else if (e.abilityId === 'ab-vw-fungal-renewal') {
            this.verdantAbilityCues.set(e.id, { cue: 'healing', remaining: 1.8 });
            this.fx.push({ pos: e.pos, ttl: 1.3, maxTtl: 1.3, color: 0xa9e878, radius: 4.2 });
            this.particles.fungalBurst(e.pos.x, e.pos.y, true);
            this.spawnFungalGroundVisual(e.pos.x, e.pos.y, true);
          } else if (e.abilityId === 'ab-vw-solar-bloom') {
            this.verdantAbilityCues.set(e.id, { cue: 'solar', remaining: 1.4 });
            this.fx.push({ pos: e.pos, ttl: 1.0, maxTtl: 1.0, color: 0xffdf72, radius: 6.2 });
            this.particles.solarBloom(e.pos.x, e.pos.y);
            this.spawnSolarLight(e.pos.x, e.pos.y);
          } else if (e.abilityId === 'ab-vw-resurrection-seed') {
            this.verdantAbilityCues.set(e.id, { cue: 'seed', remaining: 1.3 });
            this.fx.push({ pos: e.pos, ttl: 1.0, maxTtl: 1.0, color: 0xbbe883, radius: 4.8 });
            this.particles.burst(e.pos.x, e.pos.y, 0xbbe883, true);
          } else {
            this.fx.push({ pos: e.pos, ttl: 0.5, maxTtl: 0.5, color: 0x9fe8ff, radius: 2.6 });
            this.particles.burst(e.pos.x, e.pos.y, 0x9fe8ff);
          }
          break;
        case 'SquadKnockedDown':
          this.fx.push({ pos: e.pos, ttl: 0.75, maxTtl: 0.75, color: 0xd9c28a, radius: 2.8, shrink: true });
          this.particles.burst(e.pos.x, e.pos.y, 0xd9c28a, true);
          this.particles.dust(e.pos.x, e.pos.y, 1.2);
          this.addShakeAt(e.pos.x, e.pos.y, 0.22);
          break;
        case 'TrapTriggered':
          this.fx.push({ pos: e.pos, ttl: 0.5, maxTtl: 0.5, color: 0xff9b45, radius: 3.4 });
          this.particles.burst(e.pos.x, e.pos.y, 0xff9b45, true);
          this.vfx.shockwave(e.pos.x, e.pos.y, 0xff9b45, 5);
          this.vfx.fire(e.pos.x, e.pos.y, 1.2);
          this.vfx.smoke(e.pos.x, e.pos.y, 1.1);
          this.decals.add(e.pos.x, e.pos.y, SPRITE.scorch, 4, DECAL_SCORCH, 0.5, 0, -1);
          break;
        case 'BossSummoning':
          this.fx.push({ pos: e.pos, ttl: 1.0, maxTtl: 1.0, color: 0x9aca5a, radius: 5.5, shrink: true });
          this.particles.dust(e.pos.x, e.pos.y, 1.3, [0x5f7a44, 0x6e6454, 0x9a8d76]);
          this.addShakeAt(e.pos.x, e.pos.y, e.global ? 0.28 : 0.16);
          break;
        case 'BossSummoned':
          {
            const colors: Record<string, number> = {
              'boss-ic-arcturus': 0xffd76e,
              'boss-ic-iron-saint': 0xffe8a4,
              'boss-ic-crown-colossus': 0xffd76e,
              'boss-ah-veyr': 0xc98be8,
              'boss-ah-gravebound-colossus': 0xc8b8a8,
              'boss-ah-hollow-king': 0xff6c6c,
              'boss-ad-xyra': 0x73e8ff,
              'boss-ad-arbiter': 0x9dbbff,
              'boss-ad-war-engine': 0xffb35c,
              'boss-vw-thornback': 0x9aca5a,
              'boss-vw-ancient-groveback': 0xd6a84f,
              'boss-vw-worldroot-titan': 0x8fe05a,
            };
            const color = colors[e.defId] ?? 0xffe45e;
            this.fx.push({ pos: e.pos, ttl: 1.6, maxTtl: 1.6, color, radius: 8 });
            this.fx.push({ pos: e.pos, ttl: 0.9, maxTtl: 0.9, color, radius: 3.2, shrink: true, delay: 0.1 });
            this.particles.bossSummon(e.defId, e.pos.x, e.pos.y);
            // A boss arrival is the loudest moment in a match; give it the full treatment.
            this.vfx.shockwave(e.pos.x, e.pos.y, color, 14);
            this.vfx.dustPlume(e.pos.x, e.pos.y, 3);
            this.vfx.embers(e.pos.x, e.pos.y, 2.2, 1.4);
            this.vfx.flash(e.pos.x, 2.4, e.pos.y, color, 3.2);
            this.addShakeAt(e.pos.x, e.pos.y, e.defId === 'boss-vw-worldroot-titan' ? 1.2 : 0.95);
          }
          break;
        case 'UpgradeComplete':
          this.fx.push({ pos: e.pos, ttl: 1.0, maxTtl: 1.0, color: 0xffd76e, radius: 7 });
          this.particles.burst(e.pos.x, e.pos.y, 0xffd76e, true);
          break;
        case 'BarrageImpact':
          this.fx.push({ pos: e.pos, ttl: 0.7, maxTtl: 0.7, color: 0xff8a3f, radius: 5 });
          this.particles.burst(e.pos.x, e.pos.y, 0xff8a3f, true);
          this.addShakeAt(e.pos.x, e.pos.y, 0.45);
          this.vfx.shockwave(e.pos.x, e.pos.y, 0xff8a3f, 8);
          this.vfx.fire(e.pos.x, e.pos.y, 1.6);
          this.vfx.smoke(e.pos.x, e.pos.y, 2);
          this.decals.add(e.pos.x, e.pos.y, SPRITE.crater, 6, DECAL_SCORCH, 0.5, 0, -1);
          break;
        case 'SiteCaptured': {
          const site = this.sim.state.sites.find((s) => s.id === e.site);
          if (site) this.fx.push({ pos: site.pos, ttl: 1.0, maxTtl: 1.0, color: OWNER_COLORS[e.owner], radius: 10 });
          break;
        }
        default:
          break;
      }
    }
  }

  // ---------------------------------------------------------------- per-frame sync

  update(alpha: number, dt: number, selection: SelectionView, hoverPos: Vec2 | null): void {
    const st = this.sim.state;
    const map = this.sim.map;
    void hoverPos;

    const t = performance.now() / 1000;

    // ---- camera
    // camTarget/zoom are the *desired* values written by input; camSmooth/zoomSmooth are what
    // the camera is actually placed at. Pan used to write straight to the camera and each wheel
    // notch multiplied zoom instantly, which is most of why the game felt stiff.
    //
    // Clamp the desired values (not the smoothed ones) or the spring fights the clamp at the
    // map edges and the camera buzzes against the boundary.
    this.camTarget.x = Math.max(0, Math.min(map.width, this.camTarget.x));
    this.camTarget.y = Math.max(0, Math.min(map.height, this.camTarget.y));
    this.zoom = Math.max(16, Math.min(90, this.zoom));

    // Exponential smoothing, frame-rate independent and without overshoot. Reduced Motion (and
    // a smoothing of 0) drive omega high enough to be indistinguishable from the old instant
    // behaviour, which is also the right accessibility answer.
    const smoothing = this.reducedEffects ? 0 : this.cameraSmoothing;
    const panOmega = smoothing <= 0 ? 1e3 : 26 - smoothing * 10; // TEST: 26 (crisp) .. 16 (soft)
    const zoomOmega = smoothing <= 0 ? 1e3 : 20 - smoothing * 8; // TEST
    const panK = 1 - Math.exp(-panOmega * dt);
    this.camSmooth.x += (this.camTarget.x - this.camSmooth.x) * panK;
    this.camSmooth.y += (this.camTarget.y - this.camSmooth.y) * panK;
    // Zoom springs in log space so it feels multiplicative and symmetric, matching the
    // *= 0.89 / *= 1.12 wheel steps that drive it.
    const zoomK = 1 - Math.exp(-zoomOmega * dt);
    const logZoom = Math.log(this.zoomSmooth) + (Math.log(this.zoom) - Math.log(this.zoomSmooth)) * zoomK;
    this.zoomSmooth = Math.exp(logZoom);

    // Zoom-dependent pitch: the offset factor was a constant 0.62, so the view angle was
    // identical at every zoom. Leaning in when close shows the unit rigs and the new rim
    // lighting; flattening out when far keeps the battlefield readable.
    const zoomT = Math.max(0, Math.min(1, (this.zoomSmooth - 16) / (90 - 16)));
    const eased = zoomT * zoomT * (3 - 2 * zoomT);
    const offsetFactor = 0.44 + (0.86 - 0.44) * eased; // TEST
    this.camera.position.set(this.camSmooth.x, this.zoomSmooth, this.camSmooth.y + this.zoomSmooth * offsetFactor);
    this.camera.lookAt(this.camSmooth.x, 0, this.camSmooth.y);
    // translational rumble (giant footfalls / slams / summons) — orientation stays put
    if (this.shakeAmp > 0.004) {
      this.camera.position.x += Math.sin(t * 31.7) * this.shakeAmp * 0.3;
      this.camera.position.y += Math.sin(t * 39.3 + 1.3) * this.shakeAmp * 0.22;
      this.camera.position.z += Math.cos(t * 27.1 + 0.6) * this.shakeAmp * 0.3;
      this.shakeAmp *= Math.exp(-dt * 5);
    } else {
      this.shakeAmp = 0;
    }


    // ---- shadows
    // The ortho box follows the camera so texels stay dense wherever the player looks, and its
    // extent now scales with zoom. It was pinned at +-70 across a 16..90 zoom range, which both
    // wasted most of the shadow map when zoomed in and let shadows fall outside the frustum when
    // zoomed out.
    //
    // Two things make this an improvement rather than a regression, and both are required:
    //   - quantise the extent, so we are not rebuilding the projection matrix every frame; and
    //   - snap the light to whole shadow texels, or panning makes shadow edges crawl — and the
    //     crawl gets *more* visible as texel density goes up.
    const half = Math.max(26, Math.min(90, this.zoom * 0.9)); // TEST
    const quantisedHalf = Math.round(half / 4) * 4;
    if (quantisedHalf !== this.shadowHalf) {
      this.shadowHalf = quantisedHalf;
      const cam = this.sun.shadow.camera;
      cam.left = -quantisedHalf;
      cam.right = quantisedHalf;
      cam.top = quantisedHalf;
      cam.bottom = -quantisedHalf;
      cam.updateProjectionMatrix();
    }
    const texelWorldSize = (this.shadowHalf * 2) / this.sun.shadow.mapSize.x;
    const snappedX = Math.round(this.camTarget.x / texelWorldSize) * texelWorldSize;
    const snappedZ = Math.round(this.camTarget.y / texelWorldSize) * texelWorldSize;
    this.sun.position.set(snappedX + 60, 110, snappedZ + 45);
    this.sun.target.position.set(snappedX, 0, snappedZ);

    // Rim/fill sits opposite the sun and needs no snapping (it casts no shadow).
    this.rim.position.set(this.camTarget.x - 70, 55, this.camTarget.y - 50);
    this.rim.target.position.set(this.camTarget.x, 0, this.camTarget.y);

    this.skyDome.update(this.camera.position, this.sun.position, this.sun.target.position);

    const lerpPos = (prev: Vec2, cur: Vec2): Vec2 => ({
      x: prev.x + (cur.x - prev.x) * alpha,
      y: prev.y + (cur.y - prev.y) * alpha,
    });

    // ---- units
    let bodyI = 0;
    let headI = 0;
    let legI = 0;
    let armI = 0;
    let weaponI = 0;
    let bowI = 0;
    let crossbowI = 0;
    let rifleI = 0;
    let shieldI = 0;
    let heaterShieldI = 0;
    let carryI = 0;
    let shadowI = 0;
    let helmDomeI = 0;
    let helmSpikeI = 0;
    let hatI = 0;
    let bannerI = 0;

    const setInstance = (
      inst: THREE.InstancedMesh,
      i: number,
      x: number,
      y: number,
      z: number,
      scale: number,
      rotY: number,
      color?: THREE.Color,
      rotX = 0,
      rotZ = 0,
      sy?: number,
    ): void => {
      this.tmpPos.set(x, y, z);
      // YXZ: yaw first so pitch (lean) and roll (sway) act in the unit's local frame
      this.euler.set(rotX, rotY, rotZ, 'YXZ');
      this.tmpQuat.setFromEuler(this.euler);
      this.tmpScale.set(scale, sy ?? scale, scale);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      inst.setMatrixAt(i, this.tmpMat);
      if (color) {
        // Hit flash. Every instanced part routes through here, so this is the one insertion
        // point for the whole shared-humanoid path. `currentFlash` is set per squad below.
        //
        // Copy into a scratch colour — STYLES hands out cached THREE.Color objects, and
        // lerping one in place would permanently bleach that unit type for the rest of the match.
        if (this.currentFlash > 0) {
          inst.setColorAt(i, this.flashColor.copy(color).lerp(FLASH_WHITE, this.currentFlash * 0.7));
        } else {
          inst.setColorAt(i, color);
        }
      }
    };

    const seenSquads = new Set<EntId>();
    for (const s of st.squads.values()) {
      if (s.isBoss) continue; // bosses get bespoke meshes
      seenSquads.add(s.id);
      const d = unitDef(s.defId);
      const style = STYLES[s.defId];
      const p = lerpPos(s.prevPos, s.pos);
      const knockdown = knockdownPose(s);
      const members = squadMembers(s);
      const offsets = memberOffsets(d.squadMemberCount);
      const isBallista = s.defId === 'u-ic-mobile-ballista';
      const isTank = s.defId === 'u-ic-ironclad-tank';
      const isFortressRam = s.defId === 'u-ic-fortress-ram';
      const elevation = s.mountedOnWall !== null ? 4.0 : 0;

      // Struck units flash white. Squad.lastDamagedAt already exists and is written on every
      // damage application, so this needs no sim change and no new event — and it reaches the
      // client for free, because the snapshot carries whole Squad objects.
      this.currentFlash = this.reducedEffects
        ? 0
        : Math.max(0, 1 - (st.time - s.lastDamagedAt) / HIT_FLASH_SECONDS);
      // Bespoke rigs have their own materials rather than instance colours, so they flash by
      // emissive instead. applyRigFlash caches each rig's material list and no-ops when the
      // amount is unchanged, which it is on the overwhelming majority of frames.
      this.applyFlashToRigs(s.id, this.currentFlash);

      // ---- animation state (render-side only; sim untouched)
      let a = this.anim.get(s.id);
      if (!a) {
        a = {
          walkPhase: (s.id * 0.618) % 6.28,
          wheelRoll: 0,
          prevCooldown: s.attackCooldown,
          attackT: 9,
          attackCount: 0,
          yaw: s.facing,
          hasYaw: false,
          captureT: 0,
          nextCaptureFx: 0,
        };
        this.anim.set(s.id, a);
      }

      // ---- capture channel: is this squad actively taking a site? Own squads carry the
      // capture command; enemy squads online are projected to 'idle', so fall back to
      // "an uncontested site whose meter this player is moving, with the squad in radius"
      let captureSite: { pos: Vec2 } | null = null;
      if (s.targetId === null && s.mountedOnWall === null) {
        const cmd = s.command;
        if (cmd.kind === 'capture' && cmd.siteId) {
          const site = st.sites.find((x) => x.id === cmd.siteId);
          if (site && !site.contested && Math.hypot(s.pos.x - site.pos.x, s.pos.y - site.pos.y) <= TUNING.captureRadius) {
            captureSite = site;
          }
        } else if (cmd.kind === 'idle') {
          for (const site of st.sites) {
            if (site.contested) continue;
            const capturer =
              site.owner === -1
                ? site.meter > 0 && site.meter < 100
                  ? site.meterPlayer
                  : -1
                : site.control > 0 && site.control < 100
                  ? 1 - site.owner
                  : -1;
            if (capturer !== s.owner) continue;
            if (Math.hypot(s.pos.x - site.pos.x, s.pos.y - site.pos.y) <= TUNING.captureRadius) {
              captureSite = site;
              break;
            }
          }
        }
      }

      // smoothed facing: units turn toward their heading instead of snapping;
      // channeling squads turn to face the site they're taking
      if (!a.hasYaw) {
        a.yaw = s.facing;
        a.hasYaw = true;
      }
      const desiredYaw = captureSite
        ? Math.atan2(captureSite.pos.y - s.pos.y, captureSite.pos.x - s.pos.x)
        : s.facing;
      a.yaw = lerpAngle(a.yaw, desiredYaw, dt * 10);
      const facing = a.yaw;
      const rotYBody = -facing + Math.PI / 2;
      const cos = Math.cos(facing + Math.PI / 2);
      const sin = Math.sin(facing + Math.PI / 2);

      // ---- per-member deaths: a body drops at the vacated slot the moment a member is
      // lost, so the final kill only drops whoever was still standing
      if (a.prevMembers !== undefined && members < a.prevMembers && GameRenderer.corpseKindFor(s.defId) === 'body') {
        for (let mi = members; mi < a.prevMembers; mi++) {
          const deathOffsets = s.defId === 'u-vw-sproutling-swarm' ? SPROUTLING_MEMBER_OFFSETS : offsets;
          const o = deathOffsets[mi] ?? { x: 0, y: 0 };
          const wx = p.x + (o.x * cos - o.y * sin) * d.ui.scale;
          const wz = p.y + (o.x * sin + o.y * cos) * d.ui.scale;
          this.pushCorpse(s.defId, s.owner, wx, wz, facing);
          if (s.defId === 'u-vw-sproutling-swarm') this.particles.sproutlingBurst(wx, wz);
          else this.particles.death(wx, wz, OWNER_COLORS[s.owner]);
        }
      }
      a.prevMembers = members;

      // capture channel timeline + periodic owner-colored wisps at the squad's feet
      if (captureSite) {
        a.captureT += dt;
        a.nextCaptureFx -= dt;
        if (a.nextCaptureFx <= 0) {
          a.nextCaptureFx = 0.9; // TEST
          // owner request: keep the contracting ring pulse, drop the spark burst
          this.fx.push({
            pos: { x: p.x, y: p.y },
            ttl: 0.6,
            maxTtl: 0.6,
            color: OWNER_COLORS[s.owner],
            radius: 1.2,
            shrink: true,
          });
        }
      } else {
        a.captureT = 0;
        a.nextCaptureFx = 0;
      }

      const speed = Math.hypot(s.pos.x - s.prevPos.x, s.pos.y - s.prevPos.y) / TUNING.dt;
      a.walkPhase += speed * dt * 2.6; // continuous phase: no pops when speed changes
      a.wheelRoll += (speed * dt) / 0.46;
      const fired = s.attackCooldown > a.prevCooldown + 0.2; // cooldown jumped up => attack fired
      if (fired) {
        a.attackT = 0;
        a.attackCount++;
      }
      a.attackT += dt;
      a.prevCooldown = s.attackCooldown;
      const moveAmt = Math.min(1, speed / d.moveSpeed);
      const isMelee = d.attack !== undefined && d.attack.projectileSpeed === undefined;
      const isRangedUnit = d.attack !== undefined && d.attack.projectileSpeed !== undefined;
      const lungeK = isMelee ? Math.max(0, 1 - a.attackT / 0.28) : 0;
      const strike = lungeK > 0 ? Math.sin((1 - lungeK) * Math.PI) : 0; // out-and-back
      const recoil = isRangedUnit ? Math.max(0, 1 - a.attackT / 0.3) : 0;
      // giants cycle three melee moves — forward lunge, overhead smash, ground stomp
      let lungeAmp = 0.4;
      let pitchMult = 1.0;
      const giantVariant = d.sizeTag === 'giant' && isMelee ? (s.id + a.attackCount) % 3 : 0;
      if (giantVariant === 1) {
        lungeAmp = 0.15;
        pitchMult = 1.9; // big overhead arc
      } else if (giantVariant === 2) {
        lungeAmp = 0.1;
        pitchMult = 0.4; // stomp: barely swings the weapon, shakes the ground instead
      }
      if (giantVariant === 2 && fired) {
        this.particles.dust(p.x + Math.cos(facing) * 1.4, p.y + Math.sin(facing) * 1.4, 1.2);
        this.addShakeAt(p.x, p.y, 0.25);
      }
      // charge dash: kicked-up dust trail while the sim drives the forced move
      if (s.charge !== null && Math.random() < 0.4) {
        this.particles.dust(p.x - Math.cos(facing) * 1.2, p.y - Math.sin(facing) * 1.2, 1.0);
      }
      const lx = Math.cos(facing) * lungeAmp * strike;
      const lz = Math.sin(facing) * lungeAmp * strike;

      const bodyColor = style ? style.bodyColors[s.owner] : mixColor(d.ui.color, OWNER_COLORS[s.owner], 0.2);
      const headColor = style ? style.headColor : WORKER_HEAD;
      const limbColor = bodyColor.clone().multiplyScalar(0.55);

      // muzzle flash for ranged infantry (ballista/tank/mounts flash from their bespoke blocks)
      if (fired && isRangedUnit && !isBallista && !isTank && !isFortressRam && !MOUNTED_DEFS.has(s.defId) && !BESPOKE_VERDANT_SQUAD_DEFS.has(s.defId) && !isBespokeFactionSquad(s.defId)) {
        this.particles.muzzle(
          p.x + Math.cos(facing) * 0.7 * d.ui.scale,
          elevation + 1.2 * d.ui.scale,
          p.y + Math.sin(facing) * 0.7 * d.ui.scale,
        );
      }

      // ---- bespoke ironclad tank: the whole squad is one machine
      if (isTank) {
        let g = this.tankMeshes.get(s.id);
        if (!g) {
          g = makeTankGroup(s.owner);
          this.scene.add(g);
          this.tankMeshes.set(s.id, g);
        }
        g.position.set(p.x, elevation, p.y);
        g.rotation.y = rotYBody;
        g.scale.setScalar(d.ui.scale * 0.9);
        for (let wi = 0; wi < 6; wi++) {
          const wheel = g.getObjectByName(`wheel${wi}`);
          if (wheel) wheel.rotation.x = a.wheelRoll;
        }
        const turret = g.getObjectByName('turret');
        if (turret && s.targetId !== null) {
          const tgt =
            st.squads.get(s.targetId) ?? st.workers.get(s.targetId) ?? st.buildings.get(s.targetId) ?? st.mines.get(s.targetId);
          if (tgt) {
            const targetAngle = Math.atan2(tgt.pos.y - s.pos.y, tgt.pos.x - s.pos.x);
            turret.rotation.y = lerpAngle(turret.rotation.y, facing - targetAngle, dt * 8);
          }
        } else if (turret && moveAmt < 0.1) {
          turret.rotation.y = lerpAngle(turret.rotation.y, Math.sin(t * 0.28 + s.id) * 0.22, dt * 0.7);
        }
        const cannon = g.getObjectByName('cannon');
        if (cannon) cannon.position.z = 0.3 - 0.3 * Math.sin(recoil * Math.PI);
        if (fired && turret) {
          const worldYaw = rotYBody + turret.rotation.y;
          this.particles.muzzle(
            p.x + Math.sin(worldYaw) * 2.4 * d.ui.scale * 0.9,
            elevation + 1.7 * d.ui.scale * 0.9,
            p.y + Math.cos(worldYaw) * 2.4 * d.ui.scale * 0.9,
          );
        }
        continue; // no instanced parts for the tank
      }

      // ---- bespoke Fortress Ram: one roofed gatehouse machine with a recoiling lion head
      if (isFortressRam) {
        let g = this.fortressRamMeshes.get(s.id);
        if (!g) {
          g = makeFortressRamGroup(s.owner as 0 | 1);
          this.scene.add(g);
          this.fortressRamMeshes.set(s.id, g);
        }
        g.position.set(p.x, elevation, p.y);
        g.rotation.y = rotYBody;
        g.scale.setScalar(d.ui.scale * 0.72);
        for (let wi = 0; wi < 6; wi++) {
          const wheel = g.getObjectByName(`wheel${wi}`);
          if (wheel) wheel.rotation.x = a.wheelRoll;
        }
        const ram = g.getObjectByName('ram');
        if (ram) {
          ram.position.z = 0.1 + strike * 0.52;
          ram.position.y = 1.15 + Math.sin(t * 1.2 + s.id) * 0.025 * (1 - moveAmt);
        }
        if (fired) {
          this.particles.dust(
            p.x + Math.cos(facing) * 2.2 * d.ui.scale,
            p.y + Math.sin(facing) * 2.2 * d.ui.scale,
            1.4,
          );
          this.addShakeAt(p.x, p.y, 0.22);
        }
        continue;
      }

      // ---- bespoke ballista machine at the member-0 slot; crew stay instanced
      if (isBallista) {
        let g = this.ballistaMeshes.get(s.id);
        if (!g) {
          g = makeBallistaGroup(s.owner);
          this.scene.add(g);
          this.ballistaMeshes.set(s.id, g);
        }
        const o = offsets[0] ?? { x: 0, y: 0 };
        const gx = p.x + (o.x * cos - o.y * sin) * d.ui.scale;
        const gz = p.y + (o.x * sin + o.y * cos) * d.ui.scale;
        g.position.set(gx, elevation, gz);
        g.rotation.y = rotYBody;
        g.scale.setScalar(d.ui.scale);
        for (let wi = 0; wi < 4; wi++) {
          const wheel = g.getObjectByName(`wheel${wi}`);
          if (wheel) wheel.rotation.x = a.wheelRoll;
        }
        const arm = g.getObjectByName('arm');
        if (arm) {
          const recoilK = Math.max(0, 1 - a.attackT / 0.35);
          arm.rotation.x = -0.45 * Math.sin(recoilK * Math.PI) + Math.sin(t * 0.75 + s.id) * 0.018 * (1 - moveAmt) * (1 - recoilK);
        }
        if (fired) {
          this.particles.muzzle(
            gx + Math.cos(facing) * 1.6 * d.ui.scale,
            elevation + 1.1 * d.ui.scale,
            gz + Math.sin(facing) * 1.6 * d.ui.scale,
          );
        }
      }

      // ---- bespoke mounted rigs: each member is a rider astride a mount (skips instanced parts)
      if (MOUNTED_DEFS.has(s.defId)) {
        let arr = this.mountMeshes.get(s.id);
        if (!arr) {
          arr = [];
          this.mountMeshes.set(s.id, arr);
        }
        const flying = isFlyingMount(s.defId);
        const hover = flying ? 1.7 * d.ui.scale : 0;
        for (let i = 0; i < members; i++) {
          let g = arr[i];
          if (!g) {
            g = makeMountRig(s.defId, s.owner as 0 | 1, d.ui.scale);
            this.scene.add(g);
            arr[i] = g;
          }
          const o = offsets[i] ?? { x: 0, y: 0 };
          const gx = p.x + (o.x * cos - o.y * sin) * d.ui.scale;
          const gz = p.y + (o.x * sin + o.y * cos) * d.ui.scale;
          const ph = a.walkPhase + i * 2.1;
          const bob = flying
            ? Math.sin(t * 2 + s.id + i) * 0.12 * d.ui.scale
            : (Math.abs(Math.sin(ph)) * 0.08 * moveAmt + Math.sin(t * 1.45 + s.id + i) * 0.018 * (1 - moveAmt)) * d.ui.scale;
          g.position.set(gx, elevation + hover + bob, gz);
          g.rotation.y = rotYBody;
          g.rotation.z = knockdown * (i % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
          // diagonal gait (front-left + back-right lead)
          const amp = 0.035 * (1 - moveAmt) + 0.68 * moveAmt;
          const swing = Math.sin(ph) * amp;
          const legFL = g.getObjectByName('legFL');
          const legBR = g.getObjectByName('legBR');
          const legFR = g.getObjectByName('legFR');
          const legBL = g.getObjectByName('legBL');
          if (legFL) legFL.rotation.x = swing;
          if (legBR) legBR.rotation.x = swing;
          if (legFR) legFR.rotation.x = -swing;
          if (legBL) legBL.rotation.x = -swing;
          if (flying) {
            const flap = 0.35 + Math.sin(t * 8 + i * 1.3) * 0.55;
            const wl = g.getObjectByName('wingL');
            const wr = g.getObjectByName('wingR');
            if (wl) wl.rotation.z = flap;
            if (wr) wr.rotation.z = -flap;
          }
          const tail = g.getObjectByName('tail');
          if (tail) {
            tail.rotation.x = Math.sin(t * (1.7 + moveAmt * 1.8) + i) * (0.12 + moveAmt * 0.1);
            tail.rotation.y = Math.cos(t * 1.25 + i * 0.7) * 0.08 * (1 - moveAmt);
          }
          const arm = g.getObjectByName('riderArm');
          if (arm) arm.rotation.x = -1.2 * strike + Math.sin(t * 1.2 + i) * 0.035 * (1 - moveAmt);
          const rider = g.getObjectByName('rider');
          if (rider) {
            rider.rotation.x = 0.05 * Math.sin(ph) * moveAmt + Math.sin(t * 1.4 + i) * 0.018 * (1 - moveAmt);
            rider.rotation.z = Math.sin(t * 0.7 + i * 1.3) * 0.014 * (1 - moveAmt);
          }
          const mountBody = g.getObjectByName('mountBody');
          if (mountBody) mountBody.scale.y = 1 + Math.sin(t * 1.35 + i) * 0.025 * (1 - moveAmt);
          const mountHead = g.getObjectByName('mountHead');
          if (mountHead) {
            mountHead.rotation.y = Math.sin(t * 0.55 + i) * 0.1 * (1 - moveAmt);
            mountHead.rotation.x = 0.35 + Math.sin(t * 0.9 + i) * 0.035 * (1 - moveAmt);
          }
        }
        // release groups for members that have fallen
        for (let i = members; i < arr.length; i++) if (arr[i]) this.scene.remove(arr[i]);
        arr.length = members;
        if (fired && isRangedUnit) {
          this.particles.muzzle(
            p.x + Math.cos(facing) * 1.2 * d.ui.scale,
            elevation + hover + 1.2 * d.ui.scale,
            p.y + Math.sin(facing) * 1.2 * d.ui.scale,
          );
        }
        continue; // mounted squads render no instanced humanoid members
      }

      // ---- bespoke Covenant constructs/champions: paired fortress golems and five unique heroes
      if (BESPOKE_IRON_SQUAD_DEFS.has(s.defId)) {
        let rigs = this.ironSquadRigs.get(s.id);
        if (!rigs) {
          rigs = [];
          this.ironSquadRigs.set(s.id, rigs);
        }
        const formation = s.defId === 'u-ic-bastion-golems'
          ? [{ x: -0.82, y: 0 }, { x: 0.82, y: 0 }]
          : offsets;
        for (let i = 0; i < members; i++) {
          let rig = rigs[i];
          if (!rig) {
            rig = makeIronSquadRig(s.defId, s.owner as 0 | 1, i, d.ui.scale);
            this.scene.add(rig.group);
            rigs[i] = rig;
          }
          const o = formation[i] ?? offsets[i] ?? { x: 0, y: 0 };
          const gx = p.x + (o.x * cos - o.y * sin) * d.ui.scale + lx;
          const gz = p.y + (o.x * sin + o.y * cos) * d.ui.scale + lz;
          const ph = a.walkPhase + i * 1.57;
          const bob = Math.abs(Math.sin(ph)) * (s.defId === 'u-ic-bastion-golems' ? 0.025 : 0.055) * moveAmt * d.ui.scale;
          rig.group.position.set(gx, elevation + bob, gz);
          rig.group.rotation.y = rotYBody;
          rig.group.rotation.z = knockdown * (i % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
          rig.pose({ time: t, phase: ph, moveAmt, strike });
        }
        for (let i = members; i < rigs.length; i++) if (rigs[i]) this.scene.remove(rigs[i].group);
        rigs.length = members;
        continue;
      }

      // ---- Ashen/Astral faction rigs: broken, violent undead versus exact hovering geometry
      if (isBespokeFactionSquad(s.defId)) {
        let rigs = this.factionSquadRigs.get(s.id);
        if (!rigs) {
          rigs = [];
          this.factionSquadRigs.set(s.id, rigs);
        }
        for (let i = 0; i < members; i++) {
          let rig = rigs[i];
          if (!rig) {
            rig = makeFactionSquadRig(s.defId, s.owner as 0 | 1, i, d.ui.scale);
            this.scene.add(rig.group);
            rigs[i] = rig;
          }
          const o = offsets[i] ?? { x: 0, y: 0 };
          const gx = p.x + (o.x * cos - o.y * sin) * d.ui.scale + lx;
          const gz = p.y + (o.x * sin + o.y * cos) * d.ui.scale + lz;
          const ph = a.walkPhase + i * 1.43;
          const bob = Math.abs(Math.sin(ph)) * 0.04 * moveAmt * d.ui.scale;
          rig.group.position.set(gx, elevation + bob, gz);
          rig.group.rotation.y = rotYBody;
          rig.group.rotation.z = knockdown * (i % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
          rig.pose({ time: t, phase: ph, moveAmt, strike, charge: s.charge === null ? 0 : 1 });
        }
        for (let i = members; i < rigs.length; i++) {
          if (!rigs[i]) continue;
          this.scene.remove(rigs[i].group);
          disposeGroup(rigs[i].group);
        }
        rigs.length = members;
        if (fired && isRangedUnit) {
          this.particles.muzzle(
            p.x + Math.cos(facing) * 1.0 * d.ui.scale,
            elevation + 1.25 * d.ui.scale,
            p.y + Math.sin(facing) * 1.0 * d.ui.scale,
          );
        }
        continue;
      }

      // ---- bespoke Verdant squads: playful plant swarm, fungal mystics, solar spirits
      if (BESPOKE_VERDANT_SQUAD_DEFS.has(s.defId)) {
        let rigs = this.verdantSquadRigs.get(s.id);
        if (!rigs) {
          rigs = [];
          this.verdantSquadRigs.set(s.id, rigs);
        }
        const formation = s.defId === 'u-vw-sporecallers'
          ? [{ x: 0, y: 0.95 }, { x: -0.9, y: -0.7 }, { x: 0.9, y: -0.7 }]
          : s.defId === 'u-vw-sunpetal-seraphs'
            ? [{ x: 0, y: 2.0 }, { x: -2.35, y: -1.35 }, { x: 2.35, y: -1.35 }]
            : SPROUTLING_MEMBER_OFFSETS;
        const activeCue = this.verdantAbilityCues.get(s.id);
        let cue: VerdantAbilityCue = activeCue?.cue ?? null;
        if (s.telegraph?.abilityId === 'ab-vw-spore-cloud') cue = 'harmful';
        else if (s.telegraph?.abilityId === 'ab-vw-fungal-renewal') cue = 'healing';
        else if (s.telegraph?.abilityId === 'ab-vw-solar-bloom') cue = 'solar';
        else if (s.telegraph?.abilityId === 'ab-vw-resurrection-seed') cue = 'seed';
        const hover = s.defId === 'u-vw-sunpetal-seraphs' ? 1.05 * d.ui.scale : 0;
        for (let i = 0; i < members; i++) {
          let rig = rigs[i];
          if (!rig) {
            rig = makeVerdantSquadRig(s.defId, s.owner as 0 | 1, i, d.ui.scale);
            this.scene.add(rig.group);
            rigs[i] = rig;
          }
          const o = formation[i] ?? offsets[i] ?? { x: 0, y: 0 };
          const gx = p.x + (o.x * cos - o.y * sin) * d.ui.scale + lx;
          const gz = p.y + (o.x * sin + o.y * cos) * d.ui.scale + lz;
          const ph = a.walkPhase + i * 1.37;
          const bob = s.defId === 'u-vw-sunpetal-seraphs'
            ? Math.sin(t * 1.7 + i * 0.8) * 0.08 * d.ui.scale
            : Math.abs(Math.sin(ph)) * 0.045 * moveAmt * d.ui.scale;
          rig.group.position.set(gx, elevation + hover + bob, gz);
          rig.group.rotation.y = rotYBody;
          rig.group.rotation.z = knockdown * (i % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
          rig.pose({ time: t, phase: ph, moveAmt, strike, ability: cue, knockdown });
        }
        for (let i = members; i < rigs.length; i++) {
          if (!rigs[i]) continue;
          this.scene.remove(rigs[i].group);
          disposeGroup(rigs[i].group);
        }
        rigs.length = members;
        if (activeCue) {
          activeCue.remaining -= dt;
          if (activeCue.remaining <= 0) this.verdantAbilityCues.delete(s.id);
        }
        continue;
      }

      // ---- bespoke beast packs: quadrupeds with a diagonal gait and head-first melee strike
      if (BESPOKE_BEAST_DEFS.has(s.defId)) {
        let arr = this.beastMeshes.get(s.id);
        if (!arr) {
          arr = [];
          this.beastMeshes.set(s.id, arr);
        }
        // Quadrupeds are substantially longer and wider than humanoids. Keep the pack in a
        // loose 2x2 hunting formation so tusks, shoulders, and rear plates never interpenetrate.
        const beastOffsets: Vec2[] = s.defId === 'u-vw-skybloom-drakes'
          ? [{ x: 0, y: 1.35 }, { x: -1.4, y: -1.1 }, { x: 1.4, y: -1.1 }]
          : s.defId === 'u-vw-moonfang-hunters'
            ? [{ x: -0.85, y: 0.9 }, { x: 0.85, y: 0.9 }, { x: -0.85, y: -0.9 }, { x: 0.85, y: -0.9 }]
            : [
                { x: -1.05, y: 1.4 },
                { x: 1.05, y: 1.4 },
                { x: -1.05, y: -1.4 },
                { x: 1.05, y: -1.4 },
              ];
        for (let i = 0; i < members; i++) {
          let g = arr[i];
          if (!g) {
            g = makeBeastRig(s.defId, s.owner as 0 | 1, d.ui.scale);
            this.scene.add(g);
            arr[i] = g;
          }
          const o = beastOffsets[i] ?? offsets[i] ?? { x: 0, y: 0 };
          const gx = p.x + (o.x * cos - o.y * sin) * d.ui.scale + lx;
          const gz = p.y + (o.x * sin + o.y * cos) * d.ui.scale + lz;
          const ph = a.walkPhase + i * 1.7;
          const charge = s.charge === null ? 0 : 1;
          const bob = Math.abs(Math.sin(ph)) * (0.035 + charge * 0.035) * moveAmt * d.ui.scale;
          g.position.set(gx, elevation + bob, gz);
          g.rotation.y = rotYBody;
          g.rotation.z = knockdown * (i % 2 === 0 ? 1 : -1) * Math.PI * 0.48;
          poseBeastRig(g, { phase: ph, moveAmt, strike, charge, time: t + i * 0.31 });
        }
        for (let i = members; i < arr.length; i++) if (arr[i]) this.scene.remove(arr[i]);
        arr.length = members;
        continue;
      }

      const cyaw = Math.cos(rotYBody);
      const syaw = Math.sin(rotYBody);
      for (let i = 0; i < members; i++) {
        if (bodyI >= MAX_UNITS) break;
        if (isBallista && i === 0) continue; // the machine replaces member 0
        const o = offsets[i] ?? { x: 0, y: 0 };
        const ox = (o.x * cos - o.y * sin) * d.ui.scale;
        const oy = (o.x * sin + o.y * cos) * d.ui.scale;
        const px = p.x + ox + lx;
        const pz = p.y + oy + lz;
        // stride bounce + idle breathe, forward lean, side sway — desynced per member
        const ph = a.walkPhase + i * 2.1;
        const idleK = 1 - moveAmt;
        const idlePhase = t * 1.35 + s.id * 0.37 + i * 0.91;
        const breathe = Math.sin(idlePhase);
        const weightShift = Math.sin(t * 0.62 + s.id + i * 1.7);
        // capture channel: rhythmic work-pump (arms + bob) while taking a site // TEST
        const capK = captureSite ? 0.5 + 0.5 * Math.sin(a.captureT * 3.2 + i * 0.7) : 0;
        const bounce =
          Math.abs(Math.sin(ph)) * 0.09 * moveAmt + breathe * 0.026 * idleK + capK * 0.05;
        const lean = 0.14 * moveAmt + 0.35 * strike + breathe * 0.012 * idleK + (captureSite ? 0.12 : 0) + (s.charge !== null ? 0.45 : 0);
        const sway = Math.sin(ph) * 0.07 * moveAmt + weightShift * 0.025 * idleK;
        const ragY = elevation + bounce * (1 - knockdown) + 0.12 * d.ui.scale * knockdown;
        const ragLean = lean + knockdown * Math.PI * 0.48;
        setInstance(this.bodyInst, bodyI, px, ragY, pz, d.ui.scale, rotYBody, bodyColor, ragLean, sway + knockdown * 0.25);
        bodyI++;
        const idleLook = Math.sin(t * 0.48 + s.id * 0.23 + i * 1.4) * 0.13 * idleK;
        setInstance(this.headInst, headI, px, ragY + Math.max(0, breathe) * 0.008 * idleK, pz, d.ui.scale, rotYBody + idleLook, headColor, ragLean * 0.72, sway * 0.6 + knockdown * 0.2);
        headI++;

        // ---- limbs: pendulum legs + counter-swinging arms (joint-origin geometry)
        const stride = Math.sin(ph) * 0.7 * moveAmt;
        const idleSwing = (Math.sin(idlePhase * 0.92) * 0.07 + weightShift * 0.025) * idleK;
        for (const sideX of [-0.17, 0.17]) {
          if (legI >= MAX_UNITS * 2) break;
          const jx = px + sideX * cyaw * d.ui.scale;
          const jz = pz - sideX * syaw * d.ui.scale;
          const swing = (sideX < 0 ? stride : -stride) + idleSwing * 0.3;
          setInstance(this.legInst, legI, jx, ragY + (0.7 * (1 - knockdown) + 0.18 * knockdown) * d.ui.scale, jz, d.ui.scale, rotYBody, limbColor, swing + knockdown * (sideX < 0 ? -1.2 : 1.2));
          legI++;
        }
        const rangedWeapon = style?.weapon && style.weapon.tilt > 1; // rifle held level
        let weaponArmPitch = lean;
        for (const sideX of [-0.4, 0.4]) {
          if (armI >= MAX_UNITS * 2) break;
          const jx = px + sideX * cyaw * d.ui.scale;
          const jz = pz - sideX * syaw * d.ui.scale;
          const isWeaponArm = sideX < 0 && !!style?.weapon; // weapons render on local -x
          let armPitch: number;
          if (captureSite) {
            // both arms pump between waist and raised — visibly working the site
            armPitch = -0.35 - 0.85 * capK;
          } else if (isWeaponArm) {
            armPitch = rangedWeapon
              ? 1.15 - 0.5 * (recoil > 0 ? Math.sin(recoil * Math.PI) : 0)
              : 0.5 - 1.2 * lungeK + 1.0 * strike;
          } else {
            armPitch = (sideX < 0 ? -stride : stride) * 0.7 + idleSwing;
          }
          if (isWeaponArm) weaponArmPitch = lean + armPitch;
          setInstance(
            this.armInst,
            armI,
            jx,
            ragY + (1.32 * (1 - knockdown) + 0.22 * knockdown) * d.ui.scale,
            jz,
            d.ui.scale,
            rotYBody,
            i === 0 && style?.banner ? bodyColor : limbColor,
            lean + armPitch + knockdown * (sideX < 0 ? 1.0 : -1.0),
          );
          armI++;
        }
        if (style) {
          if (style.helm === 'dome' && helmDomeI < MAX_HELMS) {
            setInstance(this.helmDomeInst, helmDomeI, px, ragY, pz, d.ui.scale, rotYBody, style.helmColor, ragLean, sway + knockdown * 0.25);
            helmDomeI++;
          } else if (style.helm === 'spike' && helmSpikeI < MAX_HELMS) {
            setInstance(this.helmSpikeInst, helmSpikeI, px, ragY, pz, d.ui.scale, rotYBody, style.helmColor, ragLean, sway + knockdown * 0.25);
            helmSpikeI++;
          } else if (style.helm === 'hat' && hatI < 300) {
            setInstance(this.hatInst, hatI, px, ragY, pz, d.ui.scale, rotYBody, style.helmColor, ragLean, sway + knockdown * 0.25);
            hatI++;
          }
          if (i === 0 && style.banner && bannerI < MAX_BANNERS) {
            const flap = sway + Math.sin(t * 3 + s.id) * 0.08;
            setInstance(this.bannerInst, bannerI, px, ragY, pz, d.ui.scale, rotYBody, OWNER_COLORS_T[s.owner], ragLean, flap);
            bannerI++;
          }
        }
        const weapon = style?.weapon;
        if (weapon && weaponI < MAX_UNITS && bowI < MAX_UNITS && crossbowI < MAX_UNITS && rifleI < MAX_UNITS) {
          // Anchor the grip at the weapon-hand endpoint so bows do not float away from the arm.
          const shoulderX = px + Math.cos(facing + Math.PI / 2) * 0.4 * d.ui.scale;
          const shoulderZ = pz + Math.sin(facing + Math.PI / 2) * 0.4 * d.ui.scale;
          const handLength = 0.56 * d.ui.scale;
          const handForward = -Math.sin(weaponArmPitch) * handLength;
          const wx = shoulderX + Math.cos(facing) * handForward;
          const wz = shoulderZ + Math.sin(facing) * handForward;
          const wy = ragY + (1.32 * (1 - knockdown) + 0.22 * knockdown) * d.ui.scale - Math.cos(weaponArmPitch) * handLength;
          const pitch = weapon.tilt + (-1.1 * lungeK + 0.9 * strike) * pitchMult;
          this.tmpPos.set(wx, wy, wz);
          this.euler.set(0, rotYBody, 0);
          this.tmpQuat.setFromEuler(this.euler);
          // Bows/crossbows are authored in their real held orientation with local +z forward;
          // the legacy cone weapon is authored on +y and still needs the old axis conversion.
          this.euler.set(weapon.kind ? -0.08 * strike : pitch + Math.PI / 2, 0, weapon.kind === 'bow' ? -0.08 : 0);
          this.tmpQuat2.setFromEuler(this.euler);
          this.tmpQuat.multiply(this.tmpQuat2);
          if (weapon.kind) this.tmpScale.setScalar(d.ui.scale * (weapon.kind === 'bow' ? 0.92 : weapon.kind === 'rifle' ? 0.9 : 0.88));
          else this.tmpScale.set(weapon.r * 2, weapon.len, weapon.r * 2);
          this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
          const pool = weapon.kind === 'bow'
            ? this.bowInst
            : weapon.kind === 'crossbow'
              ? this.crossbowInst
              : weapon.kind === 'rifle'
                ? this.rifleInst
                : this.weaponInst;
          const index = weapon.kind === 'bow'
            ? bowI++
            : weapon.kind === 'crossbow'
              ? crossbowI++
              : weapon.kind === 'rifle'
                ? rifleI++
                : weaponI++;
          pool.setMatrixAt(index, this.tmpMat);
          pool.setColorAt(index, weapon.color);
        }
        if (style?.shield && style.shieldColors) {
          const isHeater = style.shield === 'heater';
          const available = isHeater ? heaterShieldI < 400 : shieldI < 400;
          if (available) {
            const sx2 = px + Math.cos(facing) * 0.5 * d.ui.scale;
            const sz2 = pz + Math.sin(facing) * 0.5 * d.ui.scale;
            const pool = isHeater ? this.heaterShieldInst : this.shieldInst;
            const index = isHeater ? heaterShieldI++ : shieldI++;
            setInstance(
              pool,
              index,
              sx2,
              ragY + (0.95 * (1 - knockdown) + 0.2 * knockdown) * d.ui.scale,
              sz2,
              d.ui.scale,
              rotYBody,
              style.shieldColors[s.owner],
              lean,
              sway,
            );
          }
        }
        if (shadowI < MAX_UNITS + 200 && elevation === 0) {
          setInstance(this.shadowInst, shadowI, px, 0.02, pz, d.ui.scale * (d.sizeTag === 'large' ? 1.9 : 1.1), 0);
          shadowI++;
        }
      }
    }

    // prune stale render-side state (dead squads)
    for (const id of this.anim.keys()) {
      if (!seenSquads.has(id)) this.anim.delete(id);
    }
    for (const [id, g] of this.ballistaMeshes) {
      if (!seenSquads.has(id)) {
        this.scene.remove(g);
        this.ballistaMeshes.delete(id);
      }
    }
    for (const [id, g] of this.tankMeshes) {
      if (!seenSquads.has(id)) {
        this.scene.remove(g);
        this.tankMeshes.delete(id);
      }
    }
    for (const [id, g] of this.fortressRamMeshes) {
      if (!seenSquads.has(id)) {
        this.scene.remove(g);
        this.fortressRamMeshes.delete(id);
      }
    }
    for (const [id, arr] of this.mountMeshes) {
      if (!seenSquads.has(id)) {
        for (const g of arr) this.scene.remove(g);
        this.mountMeshes.delete(id);
      }
    }
    for (const [id, arr] of this.beastMeshes) {
      if (!seenSquads.has(id)) {
        for (const g of arr) this.scene.remove(g);
        this.beastMeshes.delete(id);
      }
    }
    for (const [id, rigs] of this.verdantSquadRigs) {
      if (!seenSquads.has(id)) {
        for (const rig of rigs) {
          this.scene.remove(rig.group);
          disposeGroup(rig.group);
        }
        this.verdantSquadRigs.delete(id);
        this.verdantAbilityCues.delete(id);
      }
    }
    for (const [id, rigs] of this.ironSquadRigs) {
      if (!seenSquads.has(id)) {
        for (const rig of rigs) this.scene.remove(rig.group);
        this.ironSquadRigs.delete(id);
      }
    }
    for (const [id, rigs] of this.factionSquadRigs) {
      if (!seenSquads.has(id)) {
        for (const rig of rigs) {
          this.scene.remove(rig.group);
          disposeGroup(rig.group);
        }
        this.factionSquadRigs.delete(id);
      }
    }

    // workers: scurrying bounce scaled by actual speed, straw hat, skin head, tiny limbs
    const fallbackWorkerStyle = STYLES['u-ic-worker'];
    const seenWorkers = new Set<EntId>();
    for (const w of st.workers.values()) {
      if (w.state === 'sheltered' || bodyI >= MAX_UNITS) continue;
      seenWorkers.add(w.id);
      // Must be set per entity, not left over from the squad loop above — the push helper reads
      // whatever currentFlash currently holds.
      this.currentFlash = this.reducedEffects
        ? 0
        : Math.max(0, 1 - (st.time - w.lastDamagedAt) / HIT_FLASH_SECONDS);
      const workerStyle = STYLES[w.defId] ?? fallbackWorkerStyle;
      const workerLimbColor = workerStyle.bodyColors[0].clone().multiplyScalar(0.55);
      const p = lerpPos(w.prevPos, w.pos);
      const wdx = w.pos.x - w.prevPos.x;
      const wdy = w.pos.y - w.prevPos.y;
      const wSpeed = Math.hypot(wdx, wdy) / TUNING.dt;
      // face the walk direction (owner feedback); hold the last heading while standing
      let wYaw = this.workerYaw.get(w.id) ?? Math.atan2(wdy, wdx);
      if (wSpeed > 0.3) wYaw = lerpAngle(wYaw, Math.atan2(wdy, wdx), dt * 12);
      const workingTargetId = w.buildTargetId ?? w.repairTargetId;
      const workingTarget = workingTargetId === null ? undefined : st.buildings.get(workingTargetId);
      if (workingTarget && (w.state === 'building' || w.state === 'repairing')) {
        wYaw = lerpAngle(wYaw, Math.atan2(workingTarget.pos.y - w.pos.y, workingTarget.pos.x - w.pos.x), dt * 8);
      }
      this.workerYaw.set(w.id, wYaw);
      const rotW = -wYaw + Math.PI / 2;
      const cw = Math.cos(rotW);
      const sw = Math.sin(rotW);
      const wMove = Math.min(1, wSpeed / 3.2);

      // ---- bespoke per-faction worker rig (Ashen/Verdant/Astral); IC stays instanced
      if (BESPOKE_WORKER_DEFS.has(w.defId)) {
        let g = this.workerRigMeshes.get(w.id);
        if (!g) {
          g = makeWorkerRig(w.defId, w.owner as 0 | 1);
          this.scene.add(g);
          this.workerRigMeshes.set(w.id, g);
        }
        const hover = isHoverWorker(w.defId) ? 0.5 + Math.sin(t * 2.5 + w.id) * 0.08 : 0;
        g.position.set(p.x, hover, p.y);
        g.rotation.y = -wYaw + Math.PI / 2;
        const wStride = Math.sin(t * 9 + w.id) * 0.7 * wMove;
        const workerIdle = 1 - wMove;
        if (w.defId === 'u-vw-barkwright') {
          const working = w.state === 'building' || w.state === 'repairing';
          poseBarkwrightCircle(g, t, t * 9 + w.id, wMove, working);
        }
        const legL = g.getObjectByName('legL');
        const legR = g.getObjectByName('legR');
        if (legL) legL.rotation.x = wStride;
        if (legR) legR.rotation.x = -wStride;
        const halo = g.getObjectByName('halo');
        if (halo) halo.rotation.z += dt * 2;
        const workerBody = g.getObjectByName('workerBody') ?? g.getObjectByName('core');
        if (workerBody) {
          workerBody.scale.y = 1 + Math.sin(t * 1.55 + w.id) * 0.035 * workerIdle;
          workerBody.rotation.z = Math.sin(t * 0.72 + w.id) * 0.025 * workerIdle;
        }
        const workerHead = g.getObjectByName('workerHead');
        if (workerHead) workerHead.rotation.y = Math.sin(t * 0.6 + w.id) * 0.18 * workerIdle;
        const workerArmL = g.getObjectByName('workerArmL');
        const workerArmR = g.getObjectByName('workerArmR');
        if (workerArmL) workerArmL.rotation.z = Math.sin(t * 1.1 + w.id) * 0.08 * workerIdle;
        if (workerArmR) workerArmR.rotation.z = -Math.sin(t * 1.1 + w.id) * 0.08 * workerIdle;
        const carry = g.getObjectByName('carry') as THREE.Mesh | null;
        if (carry) {
          carry.visible = !!w.carrying;
          if (w.carrying) {
            (carry.material as THREE.MeshStandardMaterial).color.setHex(
              w.carrying.type === 'power' ? 0x54c2f0 : 0xbb86f5,
            );
          }
        }
        if (shadowI < MAX_UNITS + 200) {
          setInstance(this.shadowInst, shadowI, p.x, 0.02, p.y, w.defId === 'u-vw-barkwright' ? 1.25 : 0.6, 0);
          shadowI++;
        }
        continue;
      }

      const workerIdle = 1 - wMove;
      const workerBreath = Math.sin(t * 1.6 + w.id);
      const bob = Math.abs(Math.sin(t * 9 + w.id)) * 0.06 * wMove + workerBreath * 0.022 * workerIdle;
      const lean = 0.12 * wMove + workerBreath * 0.012 * workerIdle;
      const color = workerStyle.bodyColors[w.owner];
      setInstance(this.bodyInst, bodyI, p.x, bob, p.y, 0.55, rotW, color, lean);
      bodyI++;
      setInstance(this.headInst, headI, p.x, bob, p.y, 0.55, rotW + Math.sin(t * 0.55 + w.id) * 0.15 * workerIdle, workerStyle.headColor, lean);
      headI++;
      const wStride = Math.sin(t * 9 + w.id) * 0.8 * wMove;
      for (const sideX of [-0.17, 0.17]) {
        if (legI >= MAX_UNITS * 2) break;
        setInstance(
          this.legInst,
          legI,
          p.x + sideX * cw * 0.55,
          bob + 0.7 * 0.55,
          p.y - sideX * sw * 0.55,
          0.55,
          rotW,
          workerLimbColor,
          sideX < 0 ? wStride : -wStride,
        );
        legI++;
      }
      for (const sideX of [-0.4, 0.4]) {
        if (armI >= MAX_UNITS * 2) break;
        // carrying: both arms forward holding the crate
        const idleWork = Math.sin(t * 1.15 + w.id + (sideX < 0 ? 0 : Math.PI)) * 0.07 * workerIdle;
        const armPitch = w.carrying ? 0.9 : (sideX < 0 ? -wStride : wStride) * 0.7 + idleWork;
        setInstance(
          this.armInst,
          armI,
          p.x + sideX * cw * 0.55,
          bob + 1.32 * 0.55,
          p.y - sideX * sw * 0.55,
          0.55,
          rotW,
          color,
          lean + armPitch,
        );
        armI++;
      }
      if (hatI < 300) {
        setInstance(this.hatInst, hatI, p.x, bob, p.y, 0.55, rotW, workerStyle.helmColor, lean);
        hatI++;
      }
      if (w.carrying && carryI < 128) {
        const cColor = w.carrying.type === 'power' ? 0x54c2f0 : 0xbb86f5;
        // crate held in front of the worker, in its facing direction
        setInstance(
          this.carryInst,
          carryI,
          p.x + Math.cos(wYaw) * 0.22,
          bob,
          p.y + Math.sin(wYaw) * 0.22,
          0.55,
          rotW,
          new THREE.Color(cColor),
        );
        carryI++;
      }
      if (shadowI < MAX_UNITS + 200) {
        setInstance(this.shadowInst, shadowI, p.x, 0.02, p.y, 0.6, 0);
        shadowI++;
      }
    }
    // Living entities are done. Clear the flash before the projectile/mine/corpse pools write
    // through the same helper, or they inherit the last worker's value and corpses blink white.
    this.currentFlash = 0;

    for (const id of this.workerYaw.keys()) {
      if (!seenWorkers.has(id)) this.workerYaw.delete(id);
    }
    for (const [id, g] of this.workerRigMeshes) {
      if (!seenWorkers.has(id)) {
        this.scene.remove(g);
        disposeGroup(g);
        this.workerRigMeshes.delete(id);
      }
    }

    const commit = (inst: THREE.InstancedMesh, count: number): void => {
      inst.count = count;
      inst.instanceMatrix.needsUpdate = true;
      if (inst.instanceColor) inst.instanceColor.needsUpdate = true;
    };
    commit(this.bodyInst, bodyI);
    commit(this.headInst, headI);
    commit(this.legInst, legI);
    commit(this.armInst, armI);
    commit(this.weaponInst, weaponI);
    commit(this.bowInst, bowI);
    commit(this.crossbowInst, crossbowI);
    commit(this.rifleInst, rifleI);
    commit(this.shieldInst, shieldI);
    commit(this.heaterShieldInst, heaterShieldI);
    commit(this.carryInst, carryI);
    commit(this.shadowInst, shadowI);
    commit(this.helmDomeInst, helmDomeI);
    commit(this.helmSpikeInst, helmSpikeI);
    commit(this.hatInst, hatI);
    commit(this.bannerInst, bannerI);

    // ---- bosses (articulated giant rigs: gait, windup->slam attacks, crush flourish)
    const seenBosses = new Set<EntId>();
    for (const s of st.squads.values()) {
      if (!s.isBoss) continue;
      seenBosses.add(s.id);
      const d = unitDef(s.defId);
      let rig = this.bossRigs.get(s.id);
      if (!rig) {
        rig = makeBossRig(s.defId, s.owner, d.ui.scale);
        this.scene.add(rig.group);
        this.bossRigs.set(s.id, rig);
      }
      let ba = this.bossAnim.get(s.id);
      if (!ba) {
        ba = newBossAnim(s.id, s.facing);
        ba.prevCooldown = s.attackCooldown;
        this.bossAnim.set(s.id, ba);
      }
      const p = lerpPos(s.prevPos, s.pos);
      const bossKnockdown = knockdownPose(s);
      const speed = Math.hypot(s.pos.x - s.prevPos.x, s.pos.y - s.prevPos.y) / TUNING.dt;
      // giants turn ponderously — snapping reads as a glitch
      ba.yaw = lerpAngle(ba.yaw, s.facing, dt * (rig.big ? 5 : 8));
      rig.group.position.set(p.x, 0, p.y);
      rig.group.rotation.y = -ba.yaw + Math.PI / 2;
      rig.group.rotation.z = bossKnockdown * (s.id % 2 === 0 ? 1 : -1) * Math.PI * 0.48;

      const fired = s.attackCooldown > ba.prevCooldown + 0.2;
      ba.prevCooldown = s.attackCooldown;
      // cooldown approaching zero with a live target = the windup (anticipation before the hit)
      const windupWindow = Math.min(0.5, (d.attack?.period ?? 2) * 0.3);
      const winding =
        s.targetId !== null && s.attackCooldown > 0 && s.attackCooldown < windupWindow
          ? 1 - s.attackCooldown / windupWindow
          : 0;
      let targetYaw: number | null = null;
      if (s.targetId !== null) {
        const tgt =
          st.squads.get(s.targetId) ?? st.workers.get(s.targetId) ?? st.buildings.get(s.targetId) ?? st.mines.get(s.targetId);
        if (tgt) targetYaw = Math.atan2(tgt.pos.y - s.pos.y, tgt.pos.x - s.pos.x);
      }
      // the crushGrab ability's sim event drives the pick-up-and-crush flourish, so the
      // animation plays exactly when members actually die (locally and online)
      let startCrush = false;
      if (rig.canCrush && this.pendingCrush.has(s.id)) {
        startCrush = true;
        this.pendingCrush.delete(s.id);
      }

      const ev = animateBoss(rig, ba, {
        t,
        dt,
        x: p.x,
        z: p.y,
        speed,
        moveAmt: Math.min(1, speed / d.moveSpeed),
        winding,
        fired,
        facing: ba.yaw,
        targetYaw,
        reach: (d.attack?.range ?? 2.5) * 0.75,
        startCrush,
      });
      if (ev.footfall) {
        this.particles.dust(ev.footfall.x, ev.footfall.z, rig.big ? 1.3 : 0.7, rig.dustColors);
        if (rig.big) this.addShakeAt(ev.footfall.x, ev.footfall.z, 0.16);
      }
      if (ev.impact) {
        this.particles.dust(ev.impact.x, ev.impact.z, rig.big ? 2.1 : 1.1, rig.dustColors);
        this.fx.push({
          pos: { x: ev.impact.x, y: ev.impact.z },
          ttl: 0.4,
          maxTtl: 0.4,
          color: 0xd8c9a8,
          radius: rig.big ? 3.8 : 2.2,
        });
        this.addShakeAt(ev.impact.x, ev.impact.z, rig.big ? 0.55 : 0.22);
      }
      if (ev.crush) {
        // the grabbed soldier bursts apart in the fist, tinted the enemy's color
        this.particles.death(ev.crush.x, ev.crush.z, OWNER_COLORS[this.sim.enemiesOf(s.owner)[0] ?? s.owner], false, ev.crush.y);
        this.addShakeAt(ev.crush.x, ev.crush.z, 0.25);
      }
      if (ev.muzzle) {
        this.particles.muzzle(ev.muzzle.x, ev.muzzle.y, ev.muzzle.z);
        if (rig.big) this.addShakeAt(p.x, p.y, 0.3); // war-engine cannon kick
      }
    }
    for (const [id, rig] of this.bossRigs) {
      if (!seenBosses.has(id)) {
        this.scene.remove(rig.group);
        this.bossRigs.delete(id);
        this.bossAnim.delete(id);
      }
    }

    // ---- buildings
    const seen = new Set<EntId>();
    for (const b of st.buildings.values()) {
      seen.add(b.id);
      const bd = buildingDef(b.defId);
      let g = this.buildingMeshes.get(b.id);
      if (!g) {
        g = GameRenderer.makeBuildingGroup(b);
        this.scene.add(g);
        this.buildingMeshes.set(b.id, g);
      }
      const body = g.getObjectByName('body') as THREE.Group;
      const prog = b.state === 'blueprint' || b.state === 'constructing' ? Math.max(0.12, b.buildProgress) : 1;
      if (body.userData.layeredGrowth) {
        body.scale.y = 1;
        for (let layer = 0; layer < 6; layer++) {
          const course = body.getObjectByName(`growthLayer${layer}`);
          if (!course) continue;
          const growth = Math.max(0, Math.min(1, prog * 6 - layer));
          course.visible = growth > 0;
          const eased = growth * (2 - growth);
          course.scale.set(0.18 + eased * 0.82, 0.35 + eased * 0.65, 0.18 + eased * 0.82);
        }
        const anchors = body.getObjectByName('rootAnchors');
        if (anchors) anchors.scale.y = 0.12 + prog * 0.88;
      } else {
        body.scale.y = prog;
      }
      // Deterministic gate presentation follows the simulation's open fraction exactly. The
      // segmented halves retract toward their posts and telescope flat, so even an eight-metre
      // giant gate leaves no render mesh inside the active crossing lane.
      const gateWall = body.getObjectByName('gateWall');
      if (gateWall) {
        const open = THREE.MathUtils.clamp(b.gateOpenFraction, 0, 1);
        const eased = open * open * (3 - 2 * open);
        for (const name of ['gateLeafNegative', 'gateLeafPositive']) {
          const leaf = gateWall.getObjectByName(name);
          if (!leaf) continue;
          const closedZ = leaf.userData.closedZ as number;
          const openZ = leaf.userData.openZ as number;
          leaf.position.z = THREE.MathUtils.lerp(closedZ, openZ, eased);
          leaf.scale.z = THREE.MathUtils.lerp(1, 0.06, eased);
          leaf.visible = open < 0.999;
        }
        const threshold = gateWall.getObjectByName('gateThreshold') as THREE.Mesh | undefined;
        if (threshold?.material instanceof THREE.MeshBasicMaterial) {
          threshold.material.opacity = 0.1 + eased * 0.16;
        }
        gateWall.traverse((object) => {
          if (object.name !== 'gateIndicator' || !(object instanceof THREE.Mesh)) return;
          if (!(object.material instanceof THREE.MeshStandardMaterial)) return;
          // Red means sealed, amber means moving, and green means the shared nav aperture is
          // actually traversable. Do not advertise an opening until gateNavOpen agrees.
          const traversable = b.gateNavOpen ? 1 : open * 0.45;
          object.material.color.setRGB(
            THREE.MathUtils.lerp(1, 0.28, traversable),
            THREE.MathUtils.lerp(0.18, 1, traversable),
            THREE.MathUtils.lerp(0.12, 0.42, traversable),
          );
          object.material.emissive.copy(object.material.color);
          object.material.emissiveIntensity = 0.75 + Math.sin(t * 4.5 + b.id) * 0.18 + eased * 0.35;
        });
      }
      // disabled: sink slightly and dim via a stored flag (idempotent)
      const wantDim = b.state === 'disabled';
      if (g.userData.dimmed !== wantDim) {
        g.userData.dimmed = wantDim;
        const mats = new Set<THREE.MeshStandardMaterial>();
        body.traverse((o) => {
          if (o instanceof THREE.Mesh && o.material instanceof THREE.MeshStandardMaterial) mats.add(o.material);
        });
        for (const m of mats) {
          if (wantDim) {
            m.userData.baseColor = m.color.getHex();
            m.color.multiplyScalar(0.45);
          } else if (m.userData.baseColor !== undefined) {
            m.color.setHex(m.userData.baseColor as number);
          }
        }
      }
      // core turret tracks its target
      const turret = body.getObjectByName('turret');
      if (turret && b.defId === 'b-ic-barracks') turret.position.y = b.upgradeDone ? 7.05 : 5.55;
      if (turret && b.targetId !== null) {
        const target = st.squads.get(b.targetId) ?? st.workers.get(b.targetId);
        if (target) turret.rotation.y = -Math.atan2(target.pos.y - b.pos.y, target.pos.x - b.pos.x) - Math.PI / 2;
      }
      // Shared motion hooks for bespoke signature structures. These remain render-only and
      // deliberately use deterministic time-based loops rather than simulation state.
      const spinner = body.getObjectByName('spinner');
      if (spinner) spinner.rotation.y = t * 0.34 + b.id * 0.17;
      const spinnerReverse = body.getObjectByName('spinnerReverse');
      if (spinnerReverse) spinnerReverse.rotation.y = -t * 0.23 - b.id * 0.11;
      const carriage = body.getObjectByName('assemblyCarriage');
      if (carriage) carriage.position.z = Math.sin(t * 0.58 + b.id) * 1.05;
      const pump = body.getObjectByName('pump');
      if (pump) pump.scale.y = 1 + Math.sin(t * 2.2 + b.id) * 0.035;
      const pulseCore = body.getObjectByName('pulseCore') as THREE.Mesh | undefined;
      if (pulseCore) {
        const baseScale = pulseCore.userData.baseScale as THREE.Vector3 | undefined;
        const pulse = 1 + Math.sin(t * 2.05 + b.id) * 0.055;
        if (baseScale) pulseCore.scale.copy(baseScale).multiplyScalar(pulse);
        else pulseCore.scale.setScalar(pulse);
        if (pulseCore.material instanceof THREE.MeshStandardMaterial) {
          pulseCore.material.emissiveIntensity = 1.15 + Math.sin(t * 2.05 + b.id) * 0.35;
        }
      }
      // tower orb pulse + arbalest visibility; armed towers glow gold so the upgrade reads
      const orb = body.getObjectByName('orb') as THREE.Mesh | undefined;
      if (orb) {
        const pulse = 1 + Math.sin(t * 2.4 + b.id) * 0.08;
        orb.scale.setScalar(pulse);
        const om = orb.material as THREE.MeshStandardMaterial;
        om.emissiveIntensity = 1.3 + Math.sin(t * 2.4 + b.id) * 0.5;
        const wantGold = b.upgradeDone;
        if (g.userData.orbGold !== wantGold) {
          g.userData.orbGold = wantGold;
          const c = wantGold ? 0xffcf5e : OWNER_COLORS[b.owner];
          om.color.set(c);
          om.emissive.set(c);
        }
      }
      const arb = body.getObjectByName('arbalest');
      if (arb) {
        arb.visible = b.upgradeDone;
        if (b.targetId !== null) {
          const target = st.squads.get(b.targetId) ?? st.workers.get(b.targetId);
          if (target) arb.rotation.y = -Math.atan2(target.pos.y - b.pos.y, target.pos.x - b.pos.x) - Math.PI / 2;
        }
      }
      const towerArmor = body.getObjectByName('towerArmor');
      if (towerArmor) towerArmor.visible = b.upgradeDone;
      // Researched decorations become visible (barracks Veteran Drilling, Core annexes).
      // Socketed annex ids are content-driven: the old Iron Covenant ids once hardcoded here
      // no longer matched the actual Core upgrades, leaving completed models hidden forever.
      const deco = body.getObjectByName('upgradeDeco');
      if (deco) deco.visible = b.upgradeDone;
      if (b.isCore && bd.upgrades) {
        for (const up of bd.upgrades) {
          if (!up.socket) continue;
          const annex = body.getObjectByName(`annex-${up.id}`);
          if (!annex) continue;

          const complete = b.upgradesDone.includes(up.id);
          const active = b.upgradeInProgress?.id === up.id;
          annex.visible = complete || active;

          // Grow the socket outward from the existing Core while it is constructed. Because
          // each socket model's pieces are positioned relative to the Core origin, uniform
          // scaling visibly extends side annexes and raises top annexes into their final place.
          let buildProgress = complete ? 1 : 0;
          if (active) {
            const buildTime = Math.max(TUNING.dt, up.buildTime ?? TUNING.upgradeResearchSeconds);
            buildProgress = THREE.MathUtils.clamp(1 - b.upgradeInProgress!.remaining / buildTime, 0, 1);
          }
          const eased = buildProgress * buildProgress * (3 - 2 * buildProgress);
          annex.scale.setScalar(0.08 + eased * 0.92);
        }
      }
    }
    for (const [id, g] of this.buildingMeshes) {
      if (!seen.has(id)) {
        this.scene.remove(g);
        this.buildingMeshes.delete(id);
      }
    }

    // ---- ruins visibility (hidden while their wall stands)
    for (const ruin of st.ruins) {
      const g = this.ruinMeshes.get(ruin.id);
      if (g) g.visible = ruin.buildingId === null;
    }

    // ---- deposits
    for (const obj of this.scene.children) {
      if (!obj.name.startsWith('deposit-')) continue;
      const id = Number(obj.name.slice(8));
      const dep = st.deposits.get(id);
      if (!dep) obj.visible = false;
      else {
        const frac = dep.amount / dep.initialAmount;
        obj.scale.setScalar(0.4 + 0.6 * Math.max(0.15, frac));
      }
    }

    // ---- sites
    st.sites.forEach((site, i) => {
      const ring = this.siteRings[i];
      const disc = this.siteDiscs[i];
      const obelisk = this.siteObelisks[i];
      obelisk.visible = site.owner === -1; // tower replaces the obelisk when owned
      const mat = ring.material as THREE.MeshBasicMaterial;
      if (site.owner === -1) mat.color.set(site.contested ? 0xffc14d : 0x9aa0a8);
      else mat.color.set(OWNER_COLORS[site.owner]);
      const dmat = disc.material as THREE.MeshBasicMaterial;
      if (site.owner !== -1) {
        disc.scale.setScalar(Math.max(0.01, site.control / 100));
        dmat.color.set(OWNER_COLORS[site.owner]);
        dmat.opacity = 0.14;
      } else if (site.meter > 0 && site.meterPlayer !== -1) {
        disc.scale.setScalar(Math.max(0.01, site.meter / 100));
        dmat.color.set(OWNER_COLORS[site.meterPlayer as PlayerId]);
        dmat.opacity = 0.3;
      } else {
        disc.scale.setScalar(0.001);
      }
      mat.opacity = site.contested ? 0.5 + 0.4 * Math.sin(t * 8) : 0.7;
    });

    // ---- bars
    let bi = 0;
    const setBar = (
      pos: Vec2,
      width: number,
      frac: number,
      color: number,
      yOff: number,
      emphasized = false,
    ): void => {
      if (bi >= MAX_BARS) return;
      const w = width * (emphasized ? 1.18 : 1);
      const y = 2.6 + yOff + (emphasized ? 0.2 : 0);
      this.tmpPos.set(pos.x, y, pos.y);
      this.tmpQuat.copy(this.camera.quaternion);
      this.tmpScale.set(w, emphasized ? 0.48 : 0.34, 1);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.barBg.setMatrixAt(bi, this.tmpMat);
      const fw = Math.max(0.02, w * frac);
      const shift = -((w - fw) / 2);
      const right = new THREE.Vector3(1, 0, 0).applyQuaternion(this.camera.quaternion).multiplyScalar(shift);
      this.tmpPos.set(pos.x + right.x, y + right.y, pos.y + right.z);
      this.tmpScale.set(fw, emphasized ? 0.3 : 0.22, 1);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.barFg.setMatrixAt(bi, this.tmpMat);
      const readableHealthColor = frac <= 0.25 ? 0xff4b4b : frac <= 0.55 ? 0xffd84a : 0x64ff78;
      this.barFg.setColorAt(bi, new THREE.Color(emphasized ? readableHealthColor : color));
      bi++;
    };

    for (const s of st.squads.values()) {
      const damaged = s.health < s.maxHealth - 1;
      const isSelected = selection.squads.includes(s.id);
      if (!this.healthBarsEnabled && !isSelected) continue;
      if (!damaged && !isSelected) continue;
      const p = lerpPos(s.prevPos, s.pos);
      // boss bars ride above the taller rigs (rig height scales with ui.scale)
      const yOff = (s.mountedOnWall !== null ? 4 : 0) + (s.isBoss ? unitDef(s.defId).ui.scale * 2.3 - 1.6 : 0);
      setBar(
        p,
        s.isBoss ? 3.2 : 2.4,
        Math.max(0, s.health / s.maxHealth),
        OWNER_COLORS[s.owner],
        yOff,
        isSelected,
      );
    }
    for (const b of st.buildings.values()) {
      if (!this.healthBarsEnabled && !b.isCore && selection.buildingId !== b.id) continue;
      if (b.health >= b.maxHealth - 1 && selection.buildingId !== b.id) continue;
      const bd = buildingDef(b.defId);
      setBar(
        b.pos,
        footprintOf(bd, b.rot).w + 1,
        Math.max(0, b.health / b.maxHealth),
        OWNER_COLORS[b.owner],
        b.isCore ? 9 : 4.5,
        selection.buildingId === b.id,
      );
    }
    commit(this.barBg, bi);
    commit(this.barFg, bi);

    // ---- selection rings
    let ri = 0;
    const setRing = (pos: Vec2, radius: number, height = 0.06): void => {
      if (ri >= MAX_RINGS) return;
      this.tmpPos.set(pos.x, height, pos.y);
      this.tmpQuat.identity();
      this.tmpScale.set(radius, 1, radius);
      this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
      this.ringInst.setMatrixAt(ri, this.tmpMat);
      ri++;
    };
    for (const id of selection.squads) {
      const s = st.squads.get(id);
      if (s) {
        const tag = unitDef(s.defId).sizeTag;
        setRing(
          lerpPos(s.prevPos, s.pos),
          tag === 'giant' ? 3.1 : tag === 'large' ? 2.4 : 1.9,
          s.mountedOnWall !== null ? 4.06 : 0.06,
        );
      }
    }
    for (const id of selection.workers) {
      const w = st.workers.get(id);
      if (w && w.state !== 'sheltered') setRing(lerpPos(w.prevPos, w.pos), 1.1);
    }
    if (selection.buildingId !== null) {
      const b = st.buildings.get(selection.buildingId);
      if (b) setRing(b.pos, Math.max(buildingDef(b.defId).footprint.w, buildingDef(b.defId).footprint.h) / 2 + 1.2);
    }
    commit(this.ringInst, ri);

    // ---- primary-selection highlight: unmistakable pulsing gold ring on the picked
    // building / resource / wall foundation (owner request)
    {
      let hp: Vec2 | null = null;
      let hr = 2;
      if (selection.buildingId !== null) {
        const b = st.buildings.get(selection.buildingId);
        if (b) {
          hp = b.pos;
          const fp = buildingDef(b.defId).footprint;
          hr = Math.max(fp.w, fp.h) / 2 + 1.6;
        }
      } else if (selection.inspect) {
        const ins = selection.inspect;
        if (ins.kind === 'deposit') {
          const d = st.deposits.get(ins.id);
          if (d) ((hp = d.pos), (hr = 3.4));
        } else if (ins.kind === 'ruin') {
          const r = st.ruins.find((x) => x.id === ins.id);
          if (r) ((hp = r.pos), (hr = 6));
        } else if (ins.kind === 'enemyBuilding') {
          const b = st.buildings.get(ins.id);
          if (b) {
            hp = b.pos;
            const fp = buildingDef(b.defId).footprint;
            hr = Math.max(fp.w, fp.h) / 2 + 1.6;
          }
        } else if (ins.kind === 'enemySquad') {
          const s = st.squads.get(ins.id);
          if (s) ((hp = lerpPos(s.prevPos, s.pos)), (hr = 2.2));
        }
      }
      if (hp) {
        const wob = 0.5 + 0.5 * Math.sin(t * 4);
        this.highlightRing.visible = true;
        this.highlightRing.position.set(hp.x, 0.05, hp.y);
        const sc = hr * (1 + 0.06 * Math.sin(t * 4));
        this.highlightRing.scale.set(sc, 1, sc);
        (this.highlightRing.material as THREE.MeshBasicMaterial).opacity = 0.7 + 0.25 * wob;
      } else {
        this.highlightRing.visible = false;
      }
    }

    // ---- persistent deploy/rally marker while a unit-producing building is selected
    {
      const b = selection.buildingId !== null ? st.buildings.get(selection.buildingId) : undefined;
      const showRally = b !== undefined && (b.isCore || buildingDef(b.defId).roster !== undefined);
      this.rallyMarker.visible = showRally;
      if (showRally && b) this.rallyMarker.position.set(b.rally.x, 0, b.rally.y);
    }

    // ---- projectiles: bolts oriented along motion + fading additive trail quads
    let pi = 0;
    let ti = 0;
    const seenProjectiles = new Set<EntId>();
    for (const pr of st.projectiles.values()) {
      if (pi >= MAX_PROJ) break;
      seenProjectiles.add(pr.id);
      const p = lerpPos(pr.prevPos, pr.pos);
      const dx = pr.pos.x - pr.prevPos.x;
      const dz = pr.pos.y - pr.prevPos.y;
      const yaw = dx * dx + dz * dz > 1e-8 ? Math.atan2(dx, dz) : 0;

      // Ballistic arc, render-side only. The sim keeps its deterministic homing lerp and its
      // guaranteed hit; this is purely how the shot is *drawn*, so nothing about targeting,
      // damage, or multiplayer determinism changes.
      //
      // The launch point is tracked here rather than added to the sim Projectile, so sim state
      // stays exactly as it was. Progress is measured against the live target position, so a
      // moving target still produces a sane parabola.
      let origin = this.projOrigin.get(pr.id);
      if (!origin) {
        origin = { x: pr.prevPos.x, y: pr.prevPos.y };
        this.projOrigin.set(pr.id, origin);
      }
      const targetPos = this.projectileTargetPos(pr);
      let height = 0;
      if (targetPos) {
        const travelled = Math.hypot(p.x - origin.x, p.y - origin.y);
        const remaining = Math.hypot(targetPos.x - p.x, targetPos.y - p.y);
        const total = travelled + remaining;
        if (total > 1e-3) {
          const t = travelled / total;
          // Longer shots arc higher, capped so siege lobs stay on screen. // TEST
          const peak = Math.min(6, total * 0.16);
          height = 4 * t * (1 - t) * peak;
        }
      }
      const py = 1.8 + height;
      setInstance(this.projInst, pi, p.x, py, p.y, 1, yaw);
      pi++;
      this.vfx.trail(p.x, py, p.y, 0xffd9a0, 0.18);
      for (let k = 1; k <= TRAIL_SEGS; k++) {
        const f = k / (TRAIL_SEGS + 1);
        const tx = p.x - dx * f * 2.2;
        const tz = p.y - dz * f * 2.2;
        this.tmpPos.set(tx, py, tz);
        this.tmpQuat.copy(this.camera.quaternion); // billboard
        const sc = 1 - f * 0.75;
        this.tmpScale.set(sc, sc, sc);
        this.tmpMat.compose(this.tmpPos, this.tmpQuat, this.tmpScale);
        this.trailInst.setMatrixAt(ti, this.tmpMat);
        ti++;
      }
    }
    // Projectiles are deleted the moment they land, so prune launch points alongside them or
    // the map grows for the whole match.
    if (this.projOrigin.size > seenProjectiles.size) {
      for (const id of this.projOrigin.keys()) {
        if (!seenProjectiles.has(id)) this.projOrigin.delete(id);
      }
    }
    commit(this.projInst, pi);
    commit(this.trailInst, ti);

    // ---- mines
    let mi = 0;
    for (const m of st.mines.values()) {
      if (mi >= MAX_MINES) break;
      const sc = m.built ? 1 : 0.4 + 0.6 * m.buildProgress;
      setInstance(this.mineInst, mi, m.pos.x, 0.2, m.pos.y, sc, 0, mixColor(0x8a4a3a, OWNER_COLORS[m.owner], 0.5));
      mi++;
    }
    commit(this.mineInst, mi);

    // ---- corpses: topple ragdoll -> body lingers -> bone pile -> sinks away
    // giants use the same timeline through a bulkier one-piece body; wrecks skip the
    // topple and the bone stage entirely — they settle, smolder dark, and sink away
    let corpseI = 0;
    let boneI = 0;
    let giantI = 0;
    let wreckI = 0;
    for (const c of this.corpses) {
      c.age += dt;
      if (c.kind === 'wreck') {
        if (wreckI >= MAX_BIG_CORPSES) continue;
        const settle = Math.min(1, c.age / (c.fallDur * 2));
        const rotX = 0.07 * settle; // slight nose-down slump as it comes to rest
        const sink = Math.max(0, (c.age - (CORPSE_BODY_T - CORPSE_SINK_T)) / CORPSE_SINK_T) * 0.9;
        setInstance(this.wreckInst, wreckI, c.x, 0.02 - sink, c.z, c.scale, c.yaw, undefined, rotX);
        wreckI++;
      } else if (c.age < CORPSE_BODY_T) {
        const isGiant = c.kind === 'giant';
        if (isGiant ? giantI >= MAX_BIG_CORPSES : corpseI >= MAX_CORPSES) continue;
        // topple about the feet with a decaying settle wobble after impact
        const k = Math.min(1, c.age / c.fallDur);
        let rotX = (Math.PI / 2) * k * (2 - k);
        const settleAge = c.age - c.fallDur;
        if (settleAge > 0 && settleAge < 0.45) {
          rotX += Math.sin((settleAge / 0.45) * Math.PI * 2) * 0.1 * (1 - settleAge / 0.45);
        }
        // sink into the ground just before the bones take over
        const sink = Math.max(0, (c.age - (CORPSE_BODY_T - CORPSE_SINK_T)) / CORPSE_SINK_T) * 0.45;
        // bodies grey out as they decay
        const fade = 1 - 0.35 * Math.min(1, c.age / CORPSE_BODY_T);
        this.tmpColor.copy(c.color).multiplyScalar(fade);
        if (isGiant) {
          setInstance(this.giantCorpseInst, giantI, c.x, 0.02 - sink, c.z, c.scale, c.yaw, this.tmpColor, rotX);
          giantI++;
        } else {
          setInstance(this.corpseInst, corpseI, c.x, 0.02 - sink, c.z, c.scale, c.yaw, this.tmpColor, rotX);
          corpseI++;
        }
      } else {
        if (boneI >= MAX_CORPSES) continue;
        // bone pile: settles in and then LINGERS for the rest of the match (owner request).
        // Old skeletons are only recycled once the field saturates (pushCorpse ring buffer),
        // so a battlefield fills with bones over time. (A giant's pile inherits its large scale.)
        const boneAge = c.age - CORPSE_BODY_T;
        const rise = Math.min(1, boneAge / 0.6);
        setInstance(
          this.boneInst,
          boneI,
          c.x,
          0.02,
          c.z,
          c.scale * (0.75 + 0.25 * rise),
          c.yaw,
          undefined,
        );
        boneI++;
      }
    }
    // wrecks smolder then sink away; bodies/bones persist (recycled only by the corpse cap)
    this.corpses = this.corpses.filter((c) => (c.kind === 'wreck' ? c.age < CORPSE_BODY_T : true));
    commit(this.corpseInst, corpseI);
    commit(this.boneInst, boneI);
    commit(this.giantCorpseInst, giantI);
    commit(this.wreckInst, wreckI);

    // ---- zones (barrage telegraphs pulse red; buff zones tint by owner)
    const seenZones = new Set<EntId>();
    for (const z of st.zones.values()) {
      seenZones.add(z.id);
      const isBarrage = !!spellDef(z.spellId).bombardment;
      let m = this.zoneMeshes.get(z.id);
      if (!m) {
        m = new THREE.Mesh(
          new THREE.CircleGeometry(z.radius, 36),
          new THREE.MeshBasicMaterial({
            color: isBarrage ? 0xff3b2e : OWNER_COLORS[z.owner],
            transparent: true,
            opacity: 0.18,
            depthWrite: false,
          }),
        );
        m.rotation.x = -Math.PI / 2;
        m.position.set(z.pos.x, 0.05, z.pos.y);
        this.scene.add(m);
        this.zoneMeshes.set(z.id, m);
      }
      if (isBarrage) {
        const telegraphing = (z.telegraphRemaining ?? 0) > 0;
        (m.material as THREE.MeshBasicMaterial).opacity = telegraphing
          ? 0.22 + 0.18 * Math.abs(Math.sin(t * 7))
          : 0.32;
      }
    }
    for (const [id, m] of this.zoneMeshes) {
      if (!seenZones.has(id)) {
        this.scene.remove(m);
        this.zoneMeshes.delete(id);
      }
    }

    // ---- transient fx
    while (this.fxMeshes.length < this.fx.length) {
      const m = new THREE.Mesh(
        new THREE.RingGeometry(0.8, 1, 24),
        new THREE.MeshBasicMaterial({ transparent: true, depthWrite: false, side: THREE.DoubleSide }),
      );
      m.rotation.x = -Math.PI / 2;
      this.scene.add(m);
      this.fxMeshes.push(m);
    }
    for (let i = 0; i < this.fxMeshes.length; i++) {
      const m = this.fxMeshes[i];
      const f = this.fx[i];
      if (!f) {
        m.visible = false;
        continue;
      }
      if (f.delay && f.delay > 0) {
        f.delay -= dt;
        m.visible = false;
        continue;
      }
      f.ttl -= dt;
      const life = 1 - f.ttl / f.maxTtl;
      m.visible = true;
      m.position.set(f.pos.x, 0.1, f.pos.y);
      // order markers contract onto the click point; event fx expand outward
      m.scale.setScalar(f.shrink ? f.radius * (1.3 - 0.9 * life) : f.radius * (0.3 + life));
      const mat = m.material as THREE.MeshBasicMaterial;
      mat.color.set(f.color);
      mat.opacity = Math.max(0, f.shrink ? 0.9 * (1 - life * life) : 0.8 * (1 - life));
    }
    this.fx = this.fx.filter((f) => f.ttl > 0);

    for (let i = this.verdantGroundVisuals.length - 1; i >= 0; i--) {
      const visual = this.verdantGroundVisuals[i];
      visual.ttl -= dt;
      const age = visual.maxTtl - visual.ttl;
      const grow = Math.min(1, age / 0.42);
      const exit = Math.min(1, Math.max(0, visual.ttl / 0.45));
      visual.group.scale.setScalar(grow * (2 - grow) * exit);
      visual.group.rotation.y += dt * 0.035;
      const fade = Math.min(1, Math.max(0, visual.ttl / 0.7));
      visual.group.traverse((o) => {
        if (o instanceof THREE.PointLight) {
          o.intensity = ((o.userData.baseIntensity as number | undefined) ?? 1) * fade;
          return;
        }
        if (!(o instanceof THREE.Mesh)) return;
        const materials = Array.isArray(o.material) ? o.material : [o.material];
        for (const material of materials) {
          if (!material.transparent) continue;
          const baseOpacity = (material.userData.baseOpacity as number | undefined) ?? material.opacity;
          material.userData.baseOpacity = baseOpacity;
          material.opacity = baseOpacity * fade;
        }
      });
      if (visual.ttl <= 0) {
        this.scene.remove(visual.group);
        visual.group.traverse((o) => {
          if (!(o instanceof THREE.Mesh)) return;
          o.geometry.dispose();
          const materials = Array.isArray(o.material) ? o.material : [o.material];
          for (const material of materials) material.dispose();
        });
        this.verdantGroundVisuals.splice(i, 1);
      }
    }

    this.particles.update(dt);
    this.vfx.update(dt);
    this.decals.update(dt);

    this.renderFrame(dt);
  }

  /**
   * Two-stage frame: the world through the post-processing composer, then the overlay layer
   * (health bars) straight to the canvas so its toneMapped:false materials are untouched by
   * OutputPass. On Low there is no composer and this collapses to the original single call.
   */
  private renderFrame(dt: number): void {
    if (!this.postFx) {
      this.camera.layers.enableAll();
      this.renderer.render(this.scene, this.camera);
      return;
    }

    this.camera.layers.set(LAYER_WORLD);
    this.postFx.render(dt);

    // Draw the overlay over the composer's output. autoClear off preserves the colour buffer;
    // clearDepth gives the overlay a clean depth buffer (OutputPass's blit does not populate the
    // canvas depth, so the overlay must not depend on world depth — these materials are all
    // depthTest:false, which is exactly why only they belong on this layer).
    this.camera.layers.set(LAYER_OVERLAY);
    const previousAutoClear = this.renderer.autoClear;
    this.renderer.autoClear = false;
    this.renderer.clearDepth();
    this.renderer.render(this.scene, this.camera);
    this.renderer.autoClear = previousAutoClear;

    this.camera.layers.enableAll();
  }
}
